import { useState, useEffect, useCallback } from "react";
import { API } from "@/lib/constants";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import AssignWithBriefModal from "@/components/AssignWithBriefModal";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Radio,
  Zap,
  Plus,
  Eye,
  Send,
  X,
  Check,
  Edit3,
  Trash2,
  AlertTriangle,
  RefreshCw,
  ChevronRight,
  Target,
  Users,
  Mail,
  BarChart3,
  FileText,
  Loader2,
  ArrowLeft,
  UserPlus,
  BookOpen,
  Gem,
  Clock,
  Pause,
  UserCheck,
  Palette,
  Settings2,
  Award,
  TrendingUp,
  Megaphone,
  Brush,
  CalendarDays,
  Share2,
  Heart,
  MessageSquare,
  Repeat2,
  Link2,
} from "lucide-react";

// ─── Status Badge ─────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const styles = {
    new: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    reviewed: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    campaign_ready: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    campaign_building: "bg-purple-500/15 text-purple-400 border-purple-500/30",
    sent: "bg-green-500/15 text-green-300 border-green-500/30",
    dismissed: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
    handed_off: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    draft: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
    approved: "bg-green-500/15 text-green-400 border-green-500/30",
    rejected: "bg-red-500/15 text-red-400 border-red-500/30",
  };
  return (
    <Badge
      variant="outline"
      className={`text-xs font-medium ${styles[status] || "bg-zinc-500/10 text-zinc-400"}`}
      data-testid={`status-badge-${status}`}
    >
      {status?.replace(/_/g, " ")}
    </Badge>
  );
};

const UrgencyBadge = ({ urgency }) => {
  const styles = {
    high: "bg-red-500/15 text-red-400",
    medium: "bg-amber-500/15 text-amber-400",
    low: "bg-zinc-500/15 text-zinc-400",
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${styles[urgency] || ""}`}>
      {urgency}
    </span>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

export default function MissionControlPage({ user }) {
  // ─── State ────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("today");
  const [signals, setSignals] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [senders, setSenders] = useState([]);
  const [stats, setStats] = useState(null);
  const [learning, setLearning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState("founder");
  const [commandSurface, setCommandSurface] = useState(null);
  const [compoundCards, setCompoundCards] = useState([]);
  // #2 — hand a signal to a colleague via the EXISTING hand-off (real-staff-only pool).
  const [staffPool, setStaffPool] = useState([]);
  const [assignSignal, setAssignSignal] = useState(null);

  // Modals
  const [showCreateSignal, setShowCreateSignal] = useState(false);
  const [showSteeringModal, setShowSteeringModal] = useState(false);
  const [showAddSender, setShowAddSender] = useState(false);
  const [showCampaignDetail, setShowCampaignDetail] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Selected items
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  // Form state
  const [newSignal, setNewSignal] = useState({
    title: "", summary: "", source_url: "", source_name: "",
    signal_type: "policy_regulatory", sector: "energy", geography: "Nigeria",
  });
  const [steering, setSteering] = useState({
    target_seniority: "C-Suite", tone: "authoritative", angle: "", urgency: "medium",
    target_companies: "", notes: "",
  });
  const [newSender, setNewSender] = useState({ email: "", display_name: "", title: "" });
  const [approvalForm, setApprovalForm] = useState({ sender_id: "", recipients: "" });
  const [rejectReason, setRejectReason] = useState("");
  const [editForm, setEditForm] = useState({ subject: "", body_html: "" });
  const [buildingCampaign, setBuildingCampaign] = useState(false);

  // Dormant Goldmine state
  const [dormantList, setDormantList] = useState([]);
  const [dormantLoading, setDormantLoading] = useState(false);
  const [showRevivalSteering, setShowRevivalSteering] = useState(false);
  const [showDormantAction, setShowDormantAction] = useState(null); // hold, dismiss, founder_only
  const [showContentBridge, setShowContentBridge] = useState(false);
  const [showOutcomeModal, setShowOutcomeModal] = useState(false);
  const [showCoolingConfig, setShowCoolingConfig] = useState(false);
  const [selectedDormant, setSelectedDormant] = useState(null);
  const [revivalSteering, setRevivalSteering] = useState({
    target_seniority: "C-Suite", tone: "consultative", angle: "", urgency: "medium",
    overlay_signal_id: "", reference_proposal: true, notes: "",
  });
  const [dormantActionReason, setDormantActionReason] = useState("");
  const [contentBridgeForm, setContentBridgeForm] = useState({
    suggested_angle: "", publish_intent: "linkedin_post", target_audience: "", reason: "",
  });
  const [outcomeForm, setOutcomeForm] = useState({ outcome: "", notes: "" });
  const [coolingDays, setCoolingDays] = useState(14);
  const [buildingRevival, setBuildingRevival] = useState(false);

  // Distribution Flywheel state
  const [flywheelItems, setFlywheelItems] = useState([]);
  const [showFlywheelCreate, setShowFlywheelCreate] = useState(false);
  const [showFlywheelDetail, setShowFlywheelDetail] = useState(false);
  const [showEngagementModal, setShowEngagementModal] = useState(false);
  const [showFlywheelFromSignal, setShowFlywheelFromSignal] = useState(false);
  const [selectedFlywheel, setSelectedFlywheel] = useState(null);
  const [flywheelForm, setFlywheelForm] = useState({
    strategic_purpose: "", content_type: "linkedin_post", target_audience: "",
    linked_offering: "None", brand_mode: "remi_only", timing: "publish_now",
    scheduled_date: "", context_title: "", context_summary: "",
  });
  const [engagementForm, setEngagementForm] = useState({
    likes: 0, comments: 0, reposts: 0, linked_client_id: "",
    linked_opportunity_id: "", commercial_influence: "",
    led_to_conversation: false, led_to_meeting: false, led_to_proposal: false,
  });
  const [generatingDraft, setGeneratingDraft] = useState(false);
  const [pendingBridges, setPendingBridges] = useState([]);

  // Intelligence state
  const [intelligence, setIntelligence] = useState(null);
  const [signalScores, setSignalScores] = useState({});
  const [dormantScores, setDormantScores] = useState({});
  const [flywheelScores, setFlywheelScores] = useState({});
  const [refreshingIntel, setRefreshingIntel] = useState(false);
  const [readiness, setReadiness] = useState(null);

  // Sequence state
  const [sequences, setSequences] = useState([]);
  const [showSequenceDetail, setShowSequenceDetail] = useState(false);
  const [showSequenceOutcome, setShowSequenceOutcome] = useState(false);
  const [selectedSequence, setSelectedSequence] = useState(null);
  const [sequenceOutcome, setSequenceOutcome] = useState({ result: "", notes: "", meeting_created: false, proposal_created: false });
  const [buildingSequences, setBuildingSequences] = useState(false);

  // Autopilot state
  const [autopilotStatus, setAutopilotStatus] = useState(null);

  // Reflex state
  const [reflexData, setReflexData] = useState(null);

  // Judgement state
  const [judgement, setJudgement] = useState(null);

  // Account Intelligence state
  const [accountIntel, setAccountIntel] = useState(null);

  // Governance state
  const [governance, setGovernance] = useState(null);

  // Board Narrative state
  const [boardNarrative, setBoardNarrative] = useState(null);

  // ─── Data Fetching ────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sigRes, campRes, sndRes, statRes, learnRes, dormRes, fwRes, bridgeRes] = await Promise.all([
        fetch(`${API}/mission-control/signals`, { credentials: "include" }),
        fetch(`${API}/mission-control/campaigns`, { credentials: "include" }),
        fetch(`${API}/mission-control/senders`, { credentials: "include" }),
        fetch(`${API}/mission-control/stats`, { credentials: "include" }),
        fetch(`${API}/mission-control/learning`, { credentials: "include" }),
        fetch(`${API}/mission-control/dormant`, { credentials: "include" }),
        fetch(`${API}/mission-control/flywheel`, { credentials: "include" }),
        fetch(`${API}/mission-control/content-bridges`, { credentials: "include" }),
      ]);
      if (sigRes.ok) { const d = await sigRes.json(); setSignals(d.signals); }
      if (campRes.ok) { const d = await campRes.json(); setCampaigns(d.campaigns); }
      if (sndRes.ok) { const d = await sndRes.json(); setSenders(d.senders); }
      if (statRes.ok) { setStats(await statRes.json()); }
      if (learnRes.ok) { setLearning(await learnRes.json()); }
      if (dormRes.ok) { const d = await dormRes.json(); setDormantList(d.goldmines); setCoolingDays(d.cooling_days || 14); }
      if (fwRes.ok) { const d = await fwRes.json(); setFlywheelItems(d.items); }
      if (bridgeRes.ok) { const d = await bridgeRes.json(); setPendingBridges(d.bridges.filter(b => b.status === "pending")); }
      // Fetch intelligence scores
      const [intelRes, sigScRes, dormScRes, fwScRes] = await Promise.all([
        fetch(`${API}/mission-control/intelligence`, { credentials: "include" }),
        fetch(`${API}/mission-control/intelligence/signal-scores`, { credentials: "include" }),
        fetch(`${API}/mission-control/intelligence/dormant-scores`, { credentials: "include" }),
        fetch(`${API}/mission-control/intelligence/flywheel-scores`, { credentials: "include" }),
      ]);
      if (intelRes.ok) setIntelligence(await intelRes.json());
      if (sigScRes.ok) {
        const d = await sigScRes.json();
        const map = {}; d.signals.forEach(s => { map[s.signal_id] = s; }); setSignalScores(map);
      }
      if (dormScRes.ok) {
        const d = await dormScRes.json();
        const map = {}; d.dormant.forEach(s => { map[s.client_id] = s; }); setDormantScores(map);
      }
      if (fwScRes.ok) {
        const d = await fwScRes.json();
        const map = {}; d.flywheel.forEach(s => { map[s.item_id] = s; }); setFlywheelScores(map);
      }
      // Fetch readiness status
      const [readinessRes, seqRes] = await Promise.all([
        fetch(`${API}/mission-control/intelligence/readiness`, { credentials: "include" }),
        fetch(`${API}/mission-control/sequences`, { credentials: "include" }),
      ]);
      if (readinessRes.ok) setReadiness(await readinessRes.json());
      if (seqRes.ok) { const d = await seqRes.json(); setSequences(d.sequences); }
      // Fetch autopilot
      const apRes = await fetch(`${API}/mission-control/autopilot/status`, { credentials: "include" });
      if (apRes.ok) setAutopilotStatus(await apRes.json());
      // Fetch reflex interrupts
      const reflexRes = await fetch(`${API}/mission-control/reflex/interrupts`, { credentials: "include" });
      if (reflexRes.ok) setReflexData(await reflexRes.json());
      // Fetch judgement
      const judgRes = await fetch(`${API}/mission-control/judgement`, { credentials: "include" });
      if (judgRes.ok) setJudgement(await judgRes.json());
      // Fetch account intelligence
      const acctRes = await fetch(`${API}/mission-control/accounts/intelligence`, { credentials: "include" });
      if (acctRes.ok) setAccountIntel(await acctRes.json());
      // Fetch governance
      const govRes = await fetch(`${API}/mission-control/governance`, { credentials: "include" });
      if (govRes.ok) setGovernance(await govRes.json());
      // Fetch board narrative
      const bnRes = await fetch(`${API}/mission-control/board-narrative`, { credentials: "include" });
      if (bnRes.ok) setBoardNarrative(await bnRes.json());
      // Fetch command surface + compound cards
      const [csRes, ccRes] = await Promise.all([
        fetch(`${API}/mission-control/command-surface?role=${selectedRole}`, { credentials: "include" }),
        fetch(`${API}/mission-control/command-surface/compound-cards`, { credentials: "include" }),
      ]);
      if (csRes.ok) setCommandSurface(await csRes.json());
      if (ccRes.ok) { const d = await ccRes.json(); setCompoundCards(d.cards); }
    } catch (err) {
      toast.error("Failed to load Mission Control data");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  // #2 — real-staff-only pool for the signal hand-off picker (same endpoint as Today/Lens).
  useEffect(() => {
    fetch(`${API}/scrum/v2/assignable-staff`, { credentials: "include" })
      .then(r => r.ok ? r.json() : { staff: [] })
      .then(d => setStaffPool(d.staff || []))
      .catch(() => setStaffPool([]));
  }, []);
  const assignSignalToPerson = async ({ assignee_email, brief_for_assignee }) => {
    if (!assignSignal || !assignee_email) return false;
    const crmCo = (assignSignal.crm_overlay && assignSignal.crm_overlay[0] &&
      (assignSignal.crm_overlay[0].company_name || assignSignal.crm_overlay[0].organisation)) || "";
    try {
      const r = await fetch(`${API}/scrum/v2/handoff`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source_kind: "signal",
          source_id: assignSignal.signal_id,
          title: assignSignal.title || "Market signal",
          body: [assignSignal.summary, assignSignal.reason].filter(Boolean).join("\n\n"),
          note: brief_for_assignee || "",
          source_url: assignSignal.source_url || assignSignal.url || "",
          source_label: "Market signal",
          client_name: crmCo || assignSignal.company || assignSignal.organisation || "",
          assignee_email,
          section: "commercial",
        }),
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(b.detail || "Could not assign the signal"); return false; }
      const who = staffPool.find(s => s.email === assignee_email)?.name || assignee_email.split("@")[0];
      toast.success(`Assigned to ${who} — it's on their Today list to accept or decline`);
      // Optimistically transition the card to handed_off so it stays in view with the
      // "Assigned to X" indicator (never silently vanishes), then reconcile with the server.
      const sid = assignSignal.signal_id;
      setSignals(prev => prev.map(s => s.signal_id === sid
        ? { ...s, status: "handed_off", handed_off: true, handed_to_name: who } : s));
      setAssignSignal(null);
      fetchAll();
      return true;
    } catch { toast.error("Network error"); return false; }
  };

  // ─── Signal Actions ───────────────────────────────────────
  const handleCreateSignal = async () => {
    try {
      const res = await fetch(`${API}/mission-control/signals`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newSignal),
      });
      if (!res.ok) throw new Error("Failed to create signal");
      toast.success("Signal created");
      setShowCreateSignal(false);
      setNewSignal({ title: "", summary: "", source_url: "", source_name: "", signal_type: "policy_regulatory", sector: "energy", geography: "Nigeria" });
      fetchAll();
    } catch (err) { toast.error(err.message); }
  };

  const handleSubmitSteering = async () => {
    if (!selectedSignal) return;
    try {
      const res = await fetch(`${API}/mission-control/signals/${selectedSignal.signal_id}/review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...steering,
          target_companies: steering.target_companies ? steering.target_companies.split(",").map(s => s.trim()).filter(Boolean) : [],
        }),
      });
      if (!res.ok) throw new Error("Failed to save steering");
      toast.success("Steering captured");
      setShowSteeringModal(false);
      fetchAll();
    } catch (err) { toast.error(err.message); }
  };

  const handleBuildCampaign = async (signal) => {
    setBuildingCampaign(true);
    try {
      const res = await fetch(`${API}/mission-control/signals/${signal.signal_id}/build-campaign`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to build campaign");
      }
      toast.success("Campaign draft generated!");
      setActiveTab("campaigns");
      fetchAll();
    } catch (err) { toast.error(err.message); }
    setBuildingCampaign(false);
  };

  const handleDismissSignal = async (signalId) => {
    try {
      await fetch(`${API}/mission-control/signals/${signalId}/dismiss`, { method: "POST", credentials: "include" });
      toast.success("Signal dismissed");
      fetchAll();
    } catch (err) { toast.error("Failed to dismiss"); }
  };

  // ─── Campaign Actions ─────────────────────────────────────
  const handleApproveCampaign = async () => {
    if (!selectedCampaign || !approvalForm.sender_id) return;
    try {
      const recipients = approvalForm.recipients
        .split("\n")
        .filter(Boolean)
        .map(line => {
          const parts = line.split(",").map(s => s.trim());
          return { name: parts[0] || "", email: parts[1] || "", company: parts[2] || "" };
        })
        .filter(r => r.email);

      if (recipients.length === 0) {
        toast.error("Add at least one recipient (Name, Email per line)");
        return;
      }

      const res = await fetch(`${API}/mission-control/campaigns/${selectedCampaign.campaign_id}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ sender_id: approvalForm.sender_id, recipients }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Approval failed");
      }
      const result = await res.json();
      toast.success(result.message);
      setShowApproveModal(false);
      setShowCampaignDetail(false);
      fetchAll();
    } catch (err) { toast.error(err.message); }
  };

  const handleRejectCampaign = async () => {
    if (!selectedCampaign || !rejectReason.trim()) return;
    try {
      const res = await fetch(`${API}/mission-control/campaigns/${selectedCampaign.campaign_id}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reason: rejectReason }),
      });
      if (!res.ok) throw new Error("Rejection failed");
      toast.success("Campaign rejected");
      setShowRejectModal(false);
      setShowCampaignDetail(false);
      fetchAll();
    } catch (err) { toast.error(err.message); }
  };

  const handleEditCampaign = async () => {
    if (!selectedCampaign) return;
    try {
      const res = await fetch(`${API}/mission-control/campaigns/${selectedCampaign.campaign_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(editForm),
      });
      if (!res.ok) throw new Error("Edit failed");
      toast.success("Campaign updated");
      setShowEditModal(false);
      const updated = await res.json();
      setSelectedCampaign(updated);
      fetchAll();
    } catch (err) { toast.error(err.message); }
  };

  // ─── Sender Actions ───────────────────────────────────────
  const handleAddSender = async () => {
    if (!newSender.email || !newSender.display_name) return;
    try {
      const res = await fetch(`${API}/mission-control/senders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newSender),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed");
      }
      toast.success("Sender added");
      setShowAddSender(false);
      setNewSender({ email: "", display_name: "", title: "" });
      fetchAll();
    } catch (err) { toast.error(err.message); }
  };

  const handleRemoveSender = async (senderId) => {
    try {
      await fetch(`${API}/mission-control/senders/${senderId}`, { method: "DELETE", credentials: "include" });
      toast.success("Sender deactivated");
      fetchAll();
    } catch (err) { toast.error("Failed"); }
  };

  // ─── Dormant Actions ─────────────────────────────────────
  const handleDormantAction = async (clientId, action, reason = "") => {
    try {
      const res = await fetch(`${API}/mission-control/dormant/${clientId}/${action}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ reason }),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success(`${action === "hold" ? "Held" : action === "dismiss" ? "Dismissed" : "Flagged"} successfully`);
      setShowDormantAction(null); setDormantActionReason(""); fetchAll();
    } catch (err) { toast.error(err.message); }
  };

  const handleBuildRevival = async () => {
    if (!selectedDormant) return;
    setBuildingRevival(true);
    try {
      const res = await fetch(`${API}/mission-control/dormant/${selectedDormant.client_id}/build-revival`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({
          ...revivalSteering,
          overlay_signal_id: revivalSteering.overlay_signal_id || null,
        }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || "Failed"); }
      toast.success("Revival draft generated!");
      setShowRevivalSteering(false); setActiveTab("campaigns"); fetchAll();
    } catch (err) { toast.error(err.message); }
    setBuildingRevival(false);
  };

  const handleContentBridge = async () => {
    if (!selectedDormant) return;
    try {
      const res = await fetch(`${API}/mission-control/dormant/${selectedDormant.client_id}/content-bridge`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(contentBridgeForm),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success("Content bridge created");
      setShowContentBridge(false); fetchAll();
    } catch (err) { toast.error(err.message); }
  };

  const handleRecordOutcome = async () => {
    if (!selectedDormant || !outcomeForm.outcome) return;
    try {
      const res = await fetch(`${API}/mission-control/dormant/${selectedDormant.client_id}/outcome`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(outcomeForm),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success("Outcome recorded");
      setShowOutcomeModal(false); setOutcomeForm({ outcome: "", notes: "" }); fetchAll();
    } catch (err) { toast.error(err.message); }
  };

  const handleUpdateCooling = async () => {
    try {
      const res = await fetch(`${API}/mission-control/dormant/config`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ cooling_days: coolingDays }),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success(`Cooling window set to ${coolingDays} days`);
      setShowCoolingConfig(false); fetchAll();
    } catch (err) { toast.error(err.message); }
  };

  // ─── Flywheel Actions ────────────────────────────────────
  const handleCreateFlywheel = async () => {
    if (!flywheelForm.strategic_purpose) { toast.error("Strategic purpose is required"); return; }
    try {
      const res = await fetch(`${API}/mission-control/flywheel`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(flywheelForm),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Failed"); }
      toast.success("Flywheel item created");
      setShowFlywheelCreate(false); fetchAll();
    } catch (err) { toast.error(err.message); }
  };

  const handleActivateBridge = async (bridgeId) => {
    if (!flywheelForm.strategic_purpose) { toast.error("Strategic purpose is required"); return; }
    try {
      const res = await fetch(`${API}/mission-control/flywheel/from-bridge/${bridgeId}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(flywheelForm),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success("Bridge activated into flywheel");
      setShowFlywheelCreate(false); fetchAll();
    } catch (err) { toast.error(err.message); }
  };

  const handleSignalToContent = async (signalId) => {
    if (!flywheelForm.strategic_purpose) { toast.error("Strategic purpose is required"); return; }
    try {
      const res = await fetch(`${API}/mission-control/flywheel/from-signal/${signalId}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(flywheelForm),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success("Signal converted to content");
      setShowFlywheelFromSignal(false); setActiveTab("flywheel"); fetchAll();
    } catch (err) { toast.error(err.message); }
  };

  const handleGenerateDraft = async (itemId) => {
    setGeneratingDraft(true);
    try {
      const res = await fetch(`${API}/mission-control/flywheel/${itemId}/generate-draft`, {
        method: "POST", credentials: "include",
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Failed"); }
      const updated = await res.json();
      toast.success("Draft generated!");
      setSelectedFlywheel(updated); fetchAll();
    } catch (err) { toast.error(err.message); }
    setGeneratingDraft(false);
  };

  const handleFlywheelStatus = async (itemId, status) => {
    try {
      await fetch(`${API}/mission-control/flywheel/${itemId}/status`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ status }),
      });
      toast.success(`Status: ${status.replace(/_/g, " ")}`);
      fetchAll();
      if (selectedFlywheel?.item_id === itemId) {
        const r = await fetch(`${API}/mission-control/flywheel/${itemId}`, { credentials: "include" });
        if (r.ok) setSelectedFlywheel(await r.json());
      }
    } catch (err) { toast.error("Failed"); }
  };

  const handleDesignerStatus = async (itemId, status) => {
    try {
      await fetch(`${API}/mission-control/flywheel/${itemId}/designer-status`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ status }),
      });
      toast.success(`Designer: ${status.replace(/_/g, " ")}`);
      fetchAll();
    } catch (err) { toast.error("Failed"); }
  };

  const handlePublish = async (itemId) => {
    try {
      await fetch(`${API}/mission-control/flywheel/${itemId}/publish`, {
        method: "POST", credentials: "include",
      });
      toast.success("Published!");
      fetchAll();
    } catch (err) { toast.error("Failed"); }
  };

  const handleRecordEngagement = async () => {
    if (!selectedFlywheel) return;
    try {
      const res = await fetch(`${API}/mission-control/flywheel/${selectedFlywheel.item_id}/engagement`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(engagementForm),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success("Engagement recorded");
      setShowEngagementModal(false); fetchAll();
    } catch (err) { toast.error(err.message); }
  };

  // ─── Judgement Actions ────────────────────────────────────
  const handleRuleAction = async (ruleId, action) => {
    try {
      await fetch(`${API}/mission-control/judgement/rules/${ruleId}/action`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ action }),
      });
      toast.success(`Rule ${action}`); fetchAll();
    } catch { toast.error("Failed"); }
  };
  const handleBuildJudgement = async () => {
    try {
      const res = await fetch(`${API}/mission-control/judgement/build`, { method: "POST", credentials: "include" });
      const d = await res.json();
      toast.success(`Built: ${d.memories_built} memories, ${d.rules_suggested} rules`); fetchAll();
    } catch { toast.error("Failed"); }
  };

  // ─── Reflex Actions ──────────────────────────────────────
  const handleAckInterrupt = async (intId) => {
    try {
      await fetch(`${API}/mission-control/reflex/interrupts/${intId}/acknowledge`, { method: "POST", credentials: "include" });
      toast.success("Acknowledged"); fetchAll();
    } catch { toast.error("Failed"); }
  };
  const handleDismissInterrupt = async (intId) => {
    try {
      await fetch(`${API}/mission-control/reflex/interrupts/${intId}/dismiss`, { method: "POST", credentials: "include" });
      toast.success("Dismissed"); fetchAll();
    } catch { toast.error("Failed"); }
  };
  const handleScanReflex = async () => {
    try {
      const res = await fetch(`${API}/mission-control/reflex/scan`, { method: "POST", credentials: "include" });
      const d = await res.json();
      toast.success(`Scan complete: ${d.found} interrupts`); fetchAll();
    } catch { toast.error("Failed"); }
  };

  // ─── Sequence Actions ────────────────────────────────────
  const handleBuildSequences = async () => {
    setBuildingSequences(true);
    try {
      await fetch(`${API}/mission-control/sequences/build`, { method: "POST", credentials: "include" });
      toast.success("Sequences rebuilt");
      fetchAll();
    } catch (err) { toast.error("Failed"); }
    setBuildingSequences(false);
  };

  const handleApproveSequence = async (seqId) => {
    try {
      await fetch(`${API}/mission-control/sequences/${seqId}/approve`, { method: "POST", credentials: "include" });
      toast.success("Sequence approved");
      fetchAll();
      if (selectedSequence?.sequence_id === seqId) {
        const r = await fetch(`${API}/mission-control/sequences/${seqId}`, { credentials: "include" });
        if (r.ok) setSelectedSequence(await r.json());
      }
    } catch (err) { toast.error("Failed"); }
  };

  const handleExecuteStep = async (seqId, stepNum) => {
    try {
      const res = await fetch(`${API}/mission-control/sequences/${seqId}/execute-step`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ step_number: stepNum, status: "completed" }),
      });
      const d = await res.json();
      if (d.status === "aborted") { toast.error(`Step aborted: ${d.reason}`); }
      else { toast.success(d.all_done ? "Sequence completed!" : "Step completed"); }
      fetchAll();
      const r = await fetch(`${API}/mission-control/sequences/${seqId}`, { credentials: "include" });
      if (r.ok) setSelectedSequence(await r.json());
    } catch (err) { toast.error("Failed"); }
  };

  const handleSkipStep = async (seqId, stepNum) => {
    try {
      await fetch(`${API}/mission-control/sequences/${seqId}/remove-step`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ step_number: stepNum, status: "skipped" }),
      });
      toast.success("Step skipped");
      fetchAll();
    } catch (err) { toast.error("Failed"); }
  };

  const handleAbortSequence = async (seqId) => {
    try {
      await fetch(`${API}/mission-control/sequences/${seqId}/abort`, { method: "POST", credentials: "include" });
      toast.success("Sequence aborted");
      setShowSequenceDetail(false); fetchAll();
    } catch (err) { toast.error("Failed"); }
  };

  const handleSequenceOutcome = async () => {
    if (!selectedSequence || !sequenceOutcome.result) return;
    try {
      await fetch(`${API}/mission-control/sequences/${selectedSequence.sequence_id}/outcome`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(sequenceOutcome),
      });
      toast.success("Outcome recorded");
      setShowSequenceOutcome(false); fetchAll();
    } catch (err) { toast.error("Failed"); }
  };

  // ─── Intelligence Actions ─────────────────────────────────
  const handleRefreshIntel = async () => {
    setRefreshingIntel(true);
    try {
      await fetch(`${API}/mission-control/intelligence/full-refresh`, { method: "POST", credentials: "include" });
      toast.success("Full overnight refresh completed");
      fetchAll();
    } catch (err) { toast.error("Failed to refresh intelligence"); }
    setRefreshingIntel(false);
  };

  // ─── Tabs ─────────────────────────────────────────────────
  const tabs = [
    { id: "today", label: "Today", icon: Zap },
    { id: "signals", label: "Signals", icon: Radio, count: stats?.signals?.new },
    { id: "dormant", label: "Dormant Goldmine", icon: Gem, count: dormantList.length },
    { id: "flywheel", label: "Distribution Flywheel", icon: Megaphone, count: flywheelItems.filter(i => i.status === "draft" || i.status === "in_review").length },
    { id: "smart", label: "Smart Recommendations", icon: TrendingUp, count: intelligence?.smart_recommendations?.length },
    { id: "sequences", label: "Sequence Plays", icon: Share2, count: sequences.filter(s => s.status === "proposed").length },
    { id: "campaigns", label: "Campaigns", icon: FileText, count: stats?.campaigns?.draft },
    { id: "senders", label: "Senders", icon: Users, count: senders.filter(s => s.is_active).length },
    { id: "judgement", label: "Institutional Judgement", icon: Award, count: judgement?.rule_count },
    { id: "accounts", label: "Account Intelligence", icon: Users, count: accountIntel?.total_accounts },
    { id: "governance", label: "Governance", icon: Settings2 },
    { id: "board", label: "Board Narrative", icon: BarChart3 },
    { id: "learning", label: "Learning", icon: BookOpen },
  ];

  const activeSenders = senders.filter(s => s.is_active);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="mission-control-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mission-control-page">
      {/* ─── Header ──────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="mission-control-title">
            Mission Control
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Signal-to-Campaign Automation — Energy / Policy & Regulatory / Africa
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedRole} onValueChange={v => { setSelectedRole(v); }}>
            <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="role-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="founder">Leader</SelectItem>
              <SelectItem value="bd_lead">BD Lead</SelectItem>
              <SelectItem value="scrum_master">Scrum Master</SelectItem>
              <SelectItem value="designer">Designer</SelectItem>
              <SelectItem value="account_owner">Account Owner</SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="outline"
            onClick={fetchAll}
            data-testid="refresh-btn"
          >
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => setShowCreateSignal(true)}
            data-testid="create-signal-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> New Signal
          </Button>
        </div>
      </div>

      {/* ─── Stats Strip ──────────────────────────────────── */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="stats-strip">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-amber-700">{stats.signals.new}</p>
            <p className="text-xs text-amber-600">New Signals</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-blue-700">{stats.signals.reviewed}</p>
            <p className="text-xs text-blue-600">Reviewed</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-yellow-700">{stats.campaigns.draft}</p>
            <p className="text-xs text-yellow-600">Draft Campaigns</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-green-700">{stats.campaigns.approved}</p>
            <p className="text-xs text-green-600">Approved</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-slate-700">{stats.senders_active}</p>
            <p className="text-xs text-slate-600">Active Senders</p>
          </div>
        </div>
      )}

      {/* ─── Readiness Strip + Momentum Score ──────────────── */}
      {readiness && (
        <div className="flex items-center gap-4 bg-white border border-slate-200 rounded-lg px-4 py-2.5" data-testid="readiness-strip">
          {/* Momentum Score */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white ${
              readiness.morning_momentum?.score >= 65 ? "bg-emerald-500" :
              readiness.morning_momentum?.score >= 40 ? "bg-amber-500" : "bg-slate-400"
            }`} data-testid="momentum-score">
              {readiness.morning_momentum?.score || 0}
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-700">Morning Momentum</p>
              <p className="text-[10px] text-slate-500">{readiness.morning_momentum?.description}</p>
            </div>
          </div>
          <div className="h-8 w-px bg-slate-200" />
          {/* Refresh Status */}
          <div className="flex items-center gap-3 text-[10px] text-slate-500 flex-wrap flex-1">
            {readiness.last_refresh ? (
              <>
                <span>Last refresh: {new Date(readiness.last_refresh).toLocaleString()}</span>
                <span>{readiness.recommendations_created} recs</span>
                {readiness.dormant_resurfaced > 0 && <span className="text-purple-600">{readiness.dormant_resurfaced} dormant resurfaced</span>}
                {readiness.stale_expired > 0 && <span className="text-slate-400">{readiness.stale_expired} stale expired</span>}
                {readiness.drifts_detected > 0 && <span className="text-amber-600 font-medium">{readiness.drifts_detected} drift(s)</span>}
              </>
            ) : (
              <span className="text-slate-400">Intelligence not yet refreshed — click Refresh</span>
            )}
          </div>
          {/* Critical Changes */}
          {readiness.critical_changes?.length > 0 && (
            <div className="flex flex-col gap-0.5 flex-shrink-0 max-w-xs" data-testid="critical-changes">
              {readiness.critical_changes.slice(0, 3).map((ch, i) => (
                <p key={i} className="text-[10px] text-amber-700 font-medium truncate">
                  {ch.change_summary}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── Live Reflex Alerts ──────────────────────────── */}
      {reflexData && reflexData.new_count > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2" data-testid="reflex-alerts">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <p className="text-xs font-bold text-red-700">Live Reflex Alerts — {reflexData.new_count} new</p>
            </div>
            <Button size="sm" variant="ghost" className="text-xs text-red-600" onClick={handleScanReflex} data-testid="scan-reflex-btn">
              <RefreshCw className="h-3 w-3 mr-1" /> Scan Now
            </Button>
          </div>
          {reflexData.interrupts.filter(i => i.status === "new").slice(0, 5).map(intr => (
            <div key={intr.interrupt_id} className="bg-white rounded-lg border border-red-100 px-3 py-2 flex items-start justify-between gap-3"
              data-testid={`reflex-card-${intr.interrupt_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                    intr.source_type === "email_reply" ? "bg-blue-100 text-blue-700" :
                    intr.source_type === "meeting_booked" ? "bg-green-100 text-green-700" :
                    "bg-amber-100 text-amber-700"
                  }`}>{intr.source_type?.replace(/_/g, " ")}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                    intr.urgency_level === "immediate" ? "bg-red-100 text-red-700" :
                    intr.urgency_level === "founder_escalation" ? "bg-purple-100 text-purple-700" :
                    "bg-slate-100 text-slate-600"
                  }`}>{intr.urgency_level?.replace(/_/g, " ")}</span>
                  <span className="text-[10px] text-slate-400">{intr.confidence}% conf</span>
                </div>
                <p className="text-xs text-slate-800 font-medium">{intr.detail}</p>
                {intr.reflex_play?.recommended_actions && (
                  <div className="mt-1 flex gap-1 flex-wrap">
                    {intr.reflex_play.recommended_actions.slice(0, 2).map((a, i) => (
                      <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600">{a.description}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => handleAckInterrupt(intr.interrupt_id)}
                  data-testid={`ack-interrupt-${intr.interrupt_id}`}>
                  <Check className="h-3 w-3" />
                </Button>
                <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => handleDismissInterrupt(intr.interrupt_id)}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ─── Tabs ─────────────────────────────────────────── */}
      <div className="flex gap-1 border-b border-slate-200" data-testid="mission-control-tabs">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
              {tab.count > 0 && (
                <span className="bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded-full">{tab.count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* ═══════════ TODAY'S COMMAND SURFACE ═══════════ */}
      {activeTab === "today" && commandSurface && (
        <div className="space-y-4" data-testid="command-surface">
          {/* Founder Attention Queue */}
          {commandSurface.founder_attention?.length > 0 && selectedRole === "founder" && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4" data-testid="founder-queue">
              <h3 className="text-sm font-semibold text-purple-800 mb-2">Founder Attention</h3>
              <div className="space-y-2">
                {commandSurface.founder_attention.map((item, i) => (
                  <div key={i} className="bg-white rounded p-2 border border-purple-100 flex items-center gap-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${item.priority === "immediate" ? "bg-red-100 text-red-700" : item.priority === "action" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{item.priority}</span>
                    <span className="text-[10px] text-purple-500">{item.type?.replace(/_/g, " ")}</span>
                    <span className="text-xs text-purple-800 flex-1">{item.title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Smart Recommendations + Compound Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-slate-800 mb-2">Top Recommendations</h3>
              {commandSurface.top_recommendations?.length === 0 ? (
                <p className="text-xs text-slate-400">No recommendations yet</p>
              ) : (
                commandSurface.top_recommendations?.map((rec, i) => (
                  <div key={i} className="border-b border-slate-100 py-2 last:border-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${rec.confidence >= 70 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{rec.confidence}%</span>
                      <span className={`text-[10px] px-1 rounded ${rec.item_type === "signal" ? "bg-amber-50 text-amber-600" : rec.item_type === "dormant" ? "bg-purple-50 text-purple-600" : "bg-blue-50 text-blue-600"}`}>{rec.item_type}</span>
                    </div>
                    <p className="text-xs text-slate-700 mt-0.5">{rec.company_name} — <span className="text-blue-600">{rec.recommended_action?.replace(/_/g, " ")}</span></p>
                  </div>
                ))
              )}
            </div>

            {/* Compound Cards */}
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-slate-800 mb-2">Compound Plays</h3>
              {compoundCards.length === 0 ? (
                <p className="text-xs text-slate-400">No compound plays detected</p>
              ) : (
                compoundCards.slice(0, 3).map(card => (
                  <div key={card.card_id} className="border-b border-slate-100 py-2 last:border-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${card.health_score >= 70 ? "bg-emerald-500" : "bg-amber-500"}`}>{card.health_score}</div>
                      <span className="text-xs font-medium text-slate-800">{card.company_name}</span>
                      <span className="text-[10px] text-slate-400">{card.layer_count} layers</span>
                    </div>
                    {card.layers?.map((l, j) => (
                      <p key={j} className="text-[10px] text-indigo-600 ml-8">+ {l}</p>
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Signals + Sequences row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-slate-800 mb-2">Top Signals</h3>
              {commandSurface.top_signals?.map(sig => (
                <div key={sig.signal_id} className="border-b border-slate-100 py-2 last:border-0">
                  <div className="flex items-center gap-2">
                    <StatusBadge status={sig.status} />
                    <span className="text-xs text-slate-400">{sig.sector}</span>
                  </div>
                  <p className="text-xs text-slate-700 mt-0.5 truncate">{sig.title}</p>
                </div>
              ))}
              {!commandSurface.top_signals?.length && <p className="text-xs text-slate-400">No active signals</p>}
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-slate-800 mb-2">Active Sequences</h3>
              {commandSurface.top_sequences?.map(seq => (
                <div key={seq.sequence_id} className="border-b border-slate-100 py-2 last:border-0">
                  <div className="flex items-center gap-2">
                    <StatusBadge status={seq.status} />
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${seq.confidence >= 70 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{seq.confidence}%</span>
                  </div>
                  <p className="text-xs text-slate-700 mt-0.5">{seq.expected_conversion}</p>
                </div>
              ))}
              {!commandSurface.top_sequences?.length && <p className="text-xs text-slate-400">No active sequences</p>}
            </div>
          </div>

          {/* Account Risks + Reflex Alerts */}
          {(commandSurface.account_risks?.length > 0 || commandSurface.reflex_alerts?.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {commandSurface.account_risks?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-red-800 mb-2">Account Risks</h3>
                  {commandSurface.account_risks.map(risk => (
                    <div key={risk.risk_id} className="py-1.5 border-b border-red-100 last:border-0">
                      <p className="text-xs text-red-700">{risk.company_name} — {risk.detail}</p>
                    </div>
                  ))}
                </div>
              )}
              {commandSurface.reflex_alerts?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-amber-800 mb-2">Live Alerts</h3>
                  {commandSurface.reflex_alerts.map(alert => (
                    <div key={alert.interrupt_id} className="py-1.5 border-b border-amber-100 last:border-0">
                      <p className="text-xs text-amber-700">{alert.detail}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══════════ SIGNALS TAB ═══════════ */}
      {activeTab === "signals" && (
        <div className="space-y-3" data-testid="signals-list">
          {signals.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <Radio className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p className="text-lg font-medium">No signals yet</p>
              <p className="text-sm">Create a market signal to begin the campaign workflow.</p>
            </div>
          ) : (
            signals.map(signal => (
              <div
                key={signal.signal_id}
                className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                data-testid={`signal-card-${signal.signal_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <StatusBadge status={signal.status} />
                      <span className="text-xs text-slate-400">{signal.signal_type}</span>
                      <span className="text-xs text-slate-400">{signal.sector}</span>
                      <span className="text-xs text-slate-400">{signal.geography}</span>
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900 truncate">{signal.title}</h3>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{signal.summary}</p>
                    {signal.source_name && (
                      <p className="text-xs text-slate-400 mt-1">Source: {signal.source_name}</p>
                    )}
                    {signal.handed_off && (
                      <p className="text-xs text-emerald-600 mt-1 font-medium" data-testid={`signal-handed-${signal.signal_id}`}>
                        Assigned to {signal.handed_to_name || "a colleague"} — on their Today list
                      </p>
                    )}
                    {signal.steering && (
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-blue-600">Steering:</span>
                        <span className="text-xs text-slate-500">{signal.steering.target_seniority}</span>
                        <span className="text-xs text-slate-400">|</span>
                        <span className="text-xs text-slate-500">{signal.steering.tone}</span>
                        <UrgencyBadge urgency={signal.steering.urgency} />
                      </div>
                    )}
                    {signalScores[signal.signal_id] && (
                      <div className="mt-2 bg-indigo-50 rounded px-2 py-1 flex items-center gap-3" data-testid={`signal-score-${signal.signal_id}`}>
                        <span className={`text-xs font-bold ${signalScores[signal.signal_id].move_forward_likelihood >= 70 ? "text-emerald-700" : signalScores[signal.signal_id].move_forward_likelihood >= 50 ? "text-amber-700" : "text-slate-500"}`}>
                          Likelihood: {signalScores[signal.signal_id].move_forward_likelihood}%
                        </span>
                        <span className="text-[10px] text-indigo-600">{signalScores[signal.signal_id].recommendation}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!["handed_off", "dismissed"].includes(signal.status) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setAssignSignal(signal)}
                        data-testid={`assign-signal-btn-${signal.signal_id}`}
                      >
                        <UserPlus className="h-3.5 w-3.5 mr-1" /> Assign to…
                      </Button>
                    )}
                    {signal.status === "new" && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setSelectedSignal(signal); setSteering({ target_seniority: "C-Suite", tone: "authoritative", angle: "", urgency: "medium", target_companies: "", notes: "" }); setShowSteeringModal(true); }}
                          data-testid={`steer-btn-${signal.signal_id}`}
                        >
                          <Target className="h-3.5 w-3.5 mr-1" /> Steer
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDismissSignal(signal.signal_id)}
                          data-testid={`dismiss-signal-btn-${signal.signal_id}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                    {signal.status === "reviewed" && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleBuildCampaign(signal)}
                          disabled={buildingCampaign}
                          data-testid={`build-campaign-btn-${signal.signal_id}`}
                        >
                          {buildingCampaign ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Zap className="h-3.5 w-3.5 mr-1" />}
                          Campaign
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedSignal(signal);
                            setFlywheelForm(p => ({ ...p, strategic_purpose: "", context_title: signal.title, context_summary: signal.summary }));
                            setShowFlywheelFromSignal(true);
                          }}
                          data-testid={`signal-to-content-btn-${signal.signal_id}`}
                        >
                          <Megaphone className="h-3.5 w-3.5 mr-1" /> Content
                        </Button>
                      </>
                    )}
                    {signal.status === "campaign_ready" && (
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300 text-xs">
                        <Check className="h-3 w-3 mr-1" /> Campaign Ready
                      </Badge>
                    )}
                    {signal.status === "sent" && (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 text-xs">
                        <Send className="h-3 w-3 mr-1" /> Sent
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ═══════════ DORMANT GOLDMINE TAB ═══════════ */}
      {activeTab === "dormant" && (
        <div className="space-y-3" data-testid="dormant-list">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">
              Stale 90+ day opportunities Rex thinks are recoverable. Sorted by revive confidence.
            </p>
            <Button size="sm" variant="ghost" onClick={() => setShowCoolingConfig(true)} data-testid="cooling-config-btn">
              <Settings2 className="h-3.5 w-3.5 mr-1" /> Cooling: {coolingDays}d
            </Button>
          </div>
          {dormantList.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <Gem className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p className="text-lg font-medium">No dormant opportunities</p>
              <p className="text-sm">All active deals are healthy, or cooled-off deals haven't expired yet.</p>
            </div>
          ) : (
            dormantList.map(d => (
              <div
                key={d.client_id}
                className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                data-testid={`dormant-card-${d.client_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      {/* Confidence Score */}
                      <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                        d.revive_confidence >= 70 ? "bg-emerald-100 text-emerald-700" :
                        d.revive_confidence >= 50 ? "bg-amber-100 text-amber-700" :
                        "bg-red-100 text-red-700"
                      }`} data-testid={`confidence-${d.client_id}`}>
                        <TrendingUp className="h-3 w-3" />
                        {d.revive_confidence}%
                      </div>
                      <span className="text-xs text-slate-400">{d.stage}</span>
                      <span className="text-xs text-slate-400">{d.sector}</span>
                      {d.has_proposal && (
                        <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-600 border-purple-200">Proposal</Badge>
                      )}
                      {d.action === "founder_only" && (
                        <Badge variant="outline" className="text-[10px] bg-orange-50 text-orange-600 border-orange-200">Founder Only</Badge>
                      )}
                      {d.outcome && (
                        <Badge variant="outline" className={`text-[10px] ${
                          d.outcome === "responded" || d.outcome === "meeting_booked" || d.outcome === "proposal_reopened"
                            ? "bg-green-50 text-green-600 border-green-200"
                            : d.outcome === "no_response" ? "bg-slate-100 text-slate-500" : "bg-blue-50 text-blue-600 border-blue-200"
                        }`}>{d.outcome?.replace(/_/g, " ")}</Badge>
                      )}
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900">{d.company_name}</h3>
                    <p className="text-xs text-slate-500 mt-1">
                      <Clock className="h-3 w-3 inline mr-1" />
                      {d.days_dormant} days dormant | {d.country} | {d.opportunity_count} opp(s) | {d.note_count} notes
                      {d.max_deal_value > 0 && ` | ${d.max_deal_value.toLocaleString()} value`}
                    </p>
                    <div className="mt-2 bg-slate-50 rounded px-2 py-1.5">
                      <p className="text-xs text-slate-600">
                        <span className="font-medium text-slate-700">Why recoverable:</span> {d.suggested_angle}
                      </p>
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {d.dormant_reasons?.map((r, i) => (
                          <span key={i} className="text-[10px] px-1.5 py-0.5 bg-slate-200 text-slate-600 rounded">{r.replace(/_/g, " ")}</span>
                        ))}
                      </div>
                    </div>
                    {dormantScores[d.client_id] && (
                      <div className="mt-2 bg-indigo-50 rounded px-2 py-1 flex items-center gap-3" data-testid={`dormant-score-${d.client_id}`}>
                        <span className={`text-xs font-bold ${dormantScores[d.client_id].revive_probability >= 70 ? "text-emerald-700" : dormantScores[d.client_id].revive_probability >= 50 ? "text-amber-700" : "text-slate-500"}`}>
                          Revive: {dormantScores[d.client_id].revive_probability}%
                        </span>
                        <span className="text-[10px] text-indigo-600">{dormantScores[d.client_id].recommendation}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <Button
                      size="sm"
                      onClick={() => {
                        setSelectedDormant(d);
                        setRevivalSteering({ target_seniority: "C-Suite", tone: "consultative", angle: d.suggested_angle, urgency: "medium", overlay_signal_id: "", reference_proposal: d.has_proposal, notes: "" });
                        setShowRevivalSteering(true);
                      }}
                      data-testid={`revive-btn-${d.client_id}`}
                    >
                      <Zap className="h-3.5 w-3.5 mr-1" /> Revive
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setSelectedDormant(d); setDormantActionReason(""); setShowDormantAction("hold"); }}
                      data-testid={`hold-btn-${d.client_id}`}>
                      <Pause className="h-3.5 w-3.5 mr-1" /> Hold
                    </Button>
                    <Button size="sm" variant="ghost" className="text-xs" onClick={() => { setSelectedDormant(d); setDormantActionReason(""); setShowDormantAction("dismiss"); }}
                      data-testid={`dismiss-dormant-btn-${d.client_id}`}>
                      <X className="h-3.5 w-3.5 mr-1" /> Dismiss
                    </Button>
                    <Button size="sm" variant="ghost" className="text-xs" onClick={() => { setSelectedDormant(d); setShowDormantAction("founder_only"); setDormantActionReason(""); }}
                      data-testid={`founder-btn-${d.client_id}`}>
                      <UserCheck className="h-3.5 w-3.5 mr-1" /> Founder
                    </Button>
                    <Button size="sm" variant="ghost" className="text-xs" onClick={() => {
                      setSelectedDormant(d);
                      setContentBridgeForm({ suggested_angle: d.suggested_angle, publish_intent: "linkedin_post", target_audience: d.sector || "", reason: "" });
                      setShowContentBridge(true);
                    }} data-testid={`content-bridge-btn-${d.client_id}`}>
                      <Palette className="h-3.5 w-3.5 mr-1" /> Content
                    </Button>
                    {d.action === "revive" && !d.outcome && (
                      <Button size="sm" variant="outline" className="text-xs border-green-300 text-green-700" onClick={() => {
                        setSelectedDormant(d); setOutcomeForm({ outcome: "", notes: "" }); setShowOutcomeModal(true);
                      }} data-testid={`outcome-btn-${d.client_id}`}>
                        <Award className="h-3.5 w-3.5 mr-1" /> Outcome
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ═══════════ DISTRIBUTION FLYWHEEL TAB ═══════════ */}
      {activeTab === "flywheel" && (
        <div className="space-y-3" data-testid="flywheel-list">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">
              Turn commercial & market intelligence into authority, nurture, and visibility.
            </p>
            <div className="flex gap-2">
              {pendingBridges.length > 0 && (
                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300 text-xs">
                  {pendingBridges.length} bridge(s) pending
                </Badge>
              )}
              <Button size="sm" onClick={() => {
                setFlywheelForm({ strategic_purpose: "", content_type: "linkedin_post", target_audience: "", linked_offering: "None", brand_mode: "remi_only", timing: "publish_now", scheduled_date: "", context_title: "", context_summary: "" });
                setShowFlywheelCreate(true);
              }} data-testid="create-flywheel-btn">
                <Plus className="h-4 w-4 mr-1" /> New Content
              </Button>
            </div>
          </div>
          {flywheelItems.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <Megaphone className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p className="text-lg font-medium">No flywheel items yet</p>
              <p className="text-sm">Convert signals or dormant insights into content, or create manually.</p>
            </div>
          ) : (
            flywheelItems.map(fw => (
              <div
                key={fw.item_id}
                className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-sm transition-shadow cursor-pointer"
                onClick={() => { setSelectedFlywheel(fw); setShowFlywheelDetail(true); }}
                data-testid={`flywheel-card-${fw.item_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <StatusBadge status={fw.status} />
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                        fw.brand_mode === "dual_brand" ? "bg-gradient-to-r from-blue-100 to-purple-100 text-purple-700" :
                        fw.brand_mode === "remi_only" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-700"
                      }`}>{fw.brand_mode === "dual_brand" ? "Dual Brand" : fw.brand_mode === "remi_only" ? "Remi" : "RESILIGENT"}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">{fw.strategic_purpose?.replace(/_/g, " ")}</span>
                      <span className="text-[10px] text-slate-400">{fw.content_type?.replace(/_/g, " ")}</span>
                      {fw.designer_status !== "no_designer" && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                          fw.designer_status === "asset_ready" ? "bg-green-100 text-green-700" :
                          fw.designer_status === "in_design" ? "bg-blue-100 text-blue-700" :
                          "bg-yellow-100 text-yellow-700"
                        }`}>{fw.designer_status?.replace(/_/g, " ")}</span>
                      )}
                      {fw.timing !== "publish_now" && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                          <CalendarDays className="h-3 w-3 inline mr-0.5" />{fw.timing?.replace(/_/g, " ")}
                        </span>
                      )}
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900 truncate">{fw.context_title || "Untitled"}</h3>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{fw.context_summary}</p>
                    {fw.primary_draft?.hook && (
                      <p className="text-xs text-blue-600 mt-1 italic line-clamp-1">"{fw.primary_draft.hook}"</p>
                    )}
                    <p className="text-[10px] text-slate-400 mt-1">
                      Trigger: {fw.trigger_source?.replace(/_/g, " ")}
                      {fw.linked_offering !== "None" && ` | Offering: ${fw.linked_offering}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {fw.status === "draft" && !fw.primary_draft && (
                      <Button size="sm" onClick={e => { e.stopPropagation(); handleGenerateDraft(fw.item_id); }}
                        disabled={generatingDraft} data-testid={`generate-draft-btn-${fw.item_id}`}>
                        {generatingDraft ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Brush className="h-3.5 w-3.5 mr-1" />} Draft
                      </Button>
                    )}
                    {fw.status === "ready_to_publish" && (
                      <Button size="sm" variant="outline" className="border-green-300 text-green-700"
                        onClick={e => { e.stopPropagation(); handlePublish(fw.item_id); }}>
                        <Send className="h-3.5 w-3.5 mr-1" /> Publish
                      </Button>
                    )}
                    {fw.status === "published" && !fw.engagement?.likes && (
                      <Button size="sm" variant="ghost" className="text-xs"
                        onClick={e => { e.stopPropagation(); setSelectedFlywheel(fw); setEngagementForm({ likes: 0, comments: 0, reposts: 0, linked_client_id: "", linked_opportunity_id: "", commercial_influence: "", led_to_conversation: false, led_to_meeting: false, led_to_proposal: false }); setShowEngagementModal(true); }}>
                        <BarChart3 className="h-3.5 w-3.5 mr-1" /> Record
                      </Button>
                    )}
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ═══════════ SMART RECOMMENDATIONS TAB ═══════════ */}
      {activeTab === "smart" && (
        <div className="space-y-4" data-testid="smart-recommendations">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">
              AI-learned recommendations based on your team's decision patterns across all three loops.
            </p>
            <Button size="sm" variant="outline" onClick={handleRefreshIntel} disabled={refreshingIntel} data-testid="refresh-intel-btn">
              {refreshingIntel ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
              Refresh Intelligence
            </Button>
          </div>

          {/* Cross-Loop Insights */}
          {intelligence?.cross_loop_patterns?.length > 0 && (
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4" data-testid="cross-loop-insights">
              <h3 className="text-sm font-semibold text-indigo-800 mb-2">Cross-Loop Intelligence</h3>
              <div className="space-y-2">
                {intelligence.cross_loop_patterns.map((p, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${p.confidence >= 70 ? "bg-emerald-500" : "bg-amber-500"}`} />
                    <div>
                      <p className="text-xs text-indigo-700 font-medium">{p.recommendation_hint}</p>
                      <p className="text-[10px] text-indigo-500">
                        {p.trigger_loop?.replace(/_/g, " ")} → {p.influenced_loop?.replace(/_/g, " ")} | Confidence: {p.confidence}% | Evidence: {p.evidence_count}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Suppression Info */}
          {intelligence?.suppressed_count > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 text-xs text-slate-500" data-testid="suppression-info">
              {intelligence.suppressed_count} item(s) suppressed (low confidence / repeated dismissals)
            </div>
          )}

          {/* Smart Recommendation Cards */}
          {!intelligence?.smart_recommendations?.length ? (
            <div className="text-center py-16 text-slate-400">
              <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p className="text-lg font-medium">No recommendations yet</p>
              <p className="text-sm">Click "Refresh Intelligence" to generate recommendations from your decision history.</p>
            </div>
          ) : (
            intelligence.smart_recommendations.map((rec, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`recommendation-card-${i}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                        rec.confidence >= 75 ? "bg-emerald-100 text-emerald-700" :
                        rec.confidence >= 55 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"
                      }`}>{rec.confidence}%</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        rec.item_type === "signal" ? "bg-amber-50 text-amber-600" :
                        rec.item_type === "dormant" ? "bg-purple-50 text-purple-600" : "bg-blue-50 text-blue-600"
                      }`}>{rec.item_type}</span>
                      <span className="text-xs text-slate-500 truncate">{rec.company_name}</span>
                    </div>
                    <p className="text-sm font-medium text-slate-900">
                      Best Move: <span className="text-blue-700">{rec.recommended_action?.replace(/_/g, " ")}</span>
                    </p>
                    <p className="text-xs text-slate-600 mt-1">{rec.reason_summary}</p>
                    {rec.supporting_patterns?.length > 0 && (
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {rec.supporting_patterns.map((p, j) => (
                          <span key={j} className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded">{p}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ═══════════ SEQUENCE PLAYS TAB ═══════════ */}
      {activeTab === "sequences" && (
        <div className="space-y-3" data-testid="sequences-list">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">Multi-step action chains across loops. Max 3 steps, 1 cross-loop jump. Hard abort conditions protect against blind continuation.</p>
            <Button size="sm" variant="outline" onClick={handleBuildSequences} disabled={buildingSequences} data-testid="build-sequences-btn">
              {buildingSequences ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Share2 className="h-3.5 w-3.5 mr-1" />} Rebuild Sequences
            </Button>
          </div>
          {sequences.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <Share2 className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p className="text-lg font-medium">No sequences yet</p>
              <p className="text-sm">Build sequences from your signals, dormant deals, and content items.</p>
            </div>
          ) : (
            sequences.map(seq => (
              <div key={seq.sequence_id}
                className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-sm transition-shadow cursor-pointer"
                onClick={() => { setSelectedSequence(seq); setShowSequenceDetail(true); }}
                data-testid={`sequence-card-${seq.sequence_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <StatusBadge status={seq.status} />
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${seq.confidence >= 70 ? "bg-emerald-100 text-emerald-700" : seq.confidence >= 50 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                        {seq.confidence}%
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">{seq.total_steps} steps</span>
                      {seq.cross_loop_jumps > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-600">cross-loop</span>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-slate-900">{seq.expected_conversion}</p>
                    {/* Step Timeline */}
                    <div className="flex items-center gap-1 mt-2">
                      {seq.steps?.map((step, i) => (
                        <div key={i} className="flex items-center gap-1">
                          <div className={`text-[10px] px-2 py-0.5 rounded border ${
                            step.status === "completed" ? "bg-green-50 border-green-300 text-green-700" :
                            step.status === "skipped" || step.status === "aborted" ? "bg-slate-50 border-slate-200 text-slate-400 line-through" :
                            "bg-white border-slate-200 text-slate-600"
                          }`}>
                            {step.action?.replace(/_/g, " ")}
                            {step.delay_hours > 0 && <span className="text-slate-400 ml-1">+{step.delay_hours}h</span>}
                          </div>
                          {i < seq.steps.length - 1 && <ChevronRight className="h-3 w-3 text-slate-300" />}
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-indigo-600 mt-1.5 italic">{seq.rationale?.substring(0, 120)}...</p>
                    {seq.status === "approved" && autopilotStatus?.armed_sequences && (() => {
                      const armed = autopilotStatus.armed_sequences.find(a => a.sequence_id === seq.sequence_id);
                      if (!armed) return null;
                      return (
                        <div className={`mt-1.5 flex items-center gap-2 px-2 py-1 rounded text-[10px] ${armed.eligible ? "bg-emerald-50 border border-emerald-200" : "bg-slate-50 border border-slate-200"}`}
                          data-testid={`autopilot-badge-${seq.sequence_id}`}>
                          <span className={`font-bold ${armed.eligible ? "text-emerald-700" : "text-slate-500"}`}>
                            {armed.eligible ? "AUTOPILOT ARMED" : "AUTOPILOT BLOCKED"}
                          </span>
                          {armed.eligible && armed.safety_rationale?.slice(0, 2).map((r, i) => (
                            <span key={i} className="text-emerald-600">{r}</span>
                          ))}
                          {!armed.eligible && <span className="text-slate-500">{armed.reason}</span>}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {seq.status === "proposed" && (
                      <Button size="sm" onClick={e => { e.stopPropagation(); handleApproveSequence(seq.sequence_id); }} data-testid={`approve-seq-${seq.sequence_id}`}>
                        <Check className="h-3.5 w-3.5 mr-1" /> Approve
                      </Button>
                    )}
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ═══════════ CAMPAIGNS TAB ═══════════ */}
      {activeTab === "campaigns" && (
        <div className="space-y-3" data-testid="campaigns-list">
          {campaigns.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p className="text-lg font-medium">No campaigns yet</p>
              <p className="text-sm">Build a campaign from a reviewed signal.</p>
            </div>
          ) : (
            campaigns.map(camp => (
              <div
                key={camp.campaign_id}
                className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-sm transition-shadow cursor-pointer"
                onClick={() => { setSelectedCampaign(camp); setShowCampaignDetail(true); }}
                data-testid={`campaign-card-${camp.campaign_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <StatusBadge status={camp.status} />
                      {camp.sender_name && <span className="text-xs text-slate-400">via {camp.sender_name}</span>}
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900">{camp.subject}</h3>
                    <p className="text-xs text-slate-500 mt-1">Signal: {camp.signal_title}</p>
                    {camp.recipients?.length > 0 && (
                      <p className="text-xs text-slate-400 mt-1">{camp.recipients.length} recipient(s)</p>
                    )}
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0 mt-1" />
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ═══════════ SENDERS TAB ═══════════ */}
      {activeTab === "senders" && (
        <div className="space-y-3" data-testid="senders-list">
          <div className="flex justify-end">
            <Button size="sm" onClick={() => setShowAddSender(true)} data-testid="add-sender-btn">
              <UserPlus className="h-4 w-4 mr-1" /> Add Sender
            </Button>
          </div>
          {senders.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p className="text-lg font-medium">No authorized senders</p>
              <p className="text-sm">Add sender identities before approving campaigns.</p>
            </div>
          ) : (
            senders.map(sender => (
              <div
                key={sender.sender_id}
                className={`bg-white border rounded-lg p-4 flex items-center justify-between ${
                  sender.is_active ? "border-slate-200" : "border-slate-100 opacity-50"
                }`}
                data-testid={`sender-card-${sender.sender_id}`}
              >
                <div>
                  <p className="text-sm font-semibold text-slate-900">{sender.display_name}</p>
                  <p className="text-xs text-slate-500">{sender.email}</p>
                  {sender.title && <p className="text-xs text-slate-400">{sender.title}</p>}
                </div>
                <div className="flex items-center gap-2">
                  {sender.is_active ? (
                    <>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 text-xs">Active</Badge>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveSender(sender.sender_id)}
                        data-testid={`remove-sender-btn-${sender.sender_id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-red-400" />
                      </Button>
                    </>
                  ) : (
                    <Badge variant="outline" className="bg-slate-100 text-slate-500 text-xs">Inactive</Badge>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ═══════════ INSTITUTIONAL JUDGEMENT TAB ═══════════ */}
      {activeTab === "judgement" && judgement && (
        <div className="space-y-4" data-testid="judgement-panel">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">RESILIGENT's institutional DNA — how the firm thinks, not just what converts.</p>
            <Button size="sm" variant="outline" onClick={handleBuildJudgement} data-testid="build-judgement-btn">
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Rebuild
            </Button>
          </div>

          {/* Rule Challenges */}
          {judgement.rule_challenges?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="rule-challenges">
              <p className="text-xs font-bold text-amber-700 mb-2">Rule Challenges — Review Required</p>
              {judgement.rule_challenges.map((ch, i) => (
                <div key={i} className="bg-white rounded p-2 mb-1 border border-amber-100">
                  <p className="text-xs text-amber-800">{ch.challenge}</p>
                  <p className="text-[10px] text-amber-600">{ch.trigger_pattern} | {ch.dna_type?.replace(/_/g, " ")}</p>
                  <div className="flex gap-1 mt-1">
                    {ch.action_options?.map(opt => (
                      <Button key={opt} size="sm" variant="ghost" className="text-[10px] h-6 px-2"
                        onClick={() => handleRuleAction(ch.rule_id, opt)}>
                        {opt.replace(/_/g, " ")}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Founder DNA vs Firm DNA */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-purple-200 rounded-lg p-4" data-testid="founder-dna">
              <h3 className="text-sm font-semibold text-purple-800 mb-2">Founder DNA ({judgement.founder_dna_rules?.length || 0})</h3>
              <p className="text-[10px] text-purple-500 mb-2">Rules tied to founder-specific instincts and gravitas</p>
              {judgement.founder_dna_rules?.length === 0 ? (
                <p className="text-xs text-purple-300">No founder DNA rules yet</p>
              ) : (
                judgement.founder_dna_rules?.map(rule => (
                  <div key={rule.rule_id} className="border-b border-purple-100 py-2 last:border-0">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-purple-700">{rule.preferred_action}</p>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${rule.status === "approved" ? "bg-green-100 text-green-700" : rule.status === "stale" ? "bg-slate-100 text-slate-500" : "bg-amber-100 text-amber-700"}`}>
                        {rule.status}
                      </span>
                    </div>
                    <p className="text-[10px] text-purple-500">{rule.trigger_pattern} | conf: {rule.confidence}% | reinforced: {rule.reinforcement_count}x</p>
                    {rule.status === "suggested" && (
                      <div className="flex gap-1 mt-1">
                        <Button size="sm" variant="ghost" className="text-[10px] h-6 px-2 text-green-600" onClick={() => handleRuleAction(rule.rule_id, "approve")}>Approve</Button>
                        <Button size="sm" variant="ghost" className="text-[10px] h-6 px-2 text-red-500" onClick={() => handleRuleAction(rule.rule_id, "retire")}>Retire</Button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="bg-white border border-blue-200 rounded-lg p-4" data-testid="firm-dna">
              <h3 className="text-sm font-semibold text-blue-800 mb-2">Firm DNA ({judgement.firm_dna_rules?.length || 0})</h3>
              <p className="text-[10px] text-blue-500 mb-2">Rules any authorized operator can apply</p>
              {judgement.firm_dna_rules?.length === 0 ? (
                <p className="text-xs text-blue-300">No firm DNA rules yet</p>
              ) : (
                judgement.firm_dna_rules?.map(rule => (
                  <div key={rule.rule_id} className="border-b border-blue-100 py-2 last:border-0">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-blue-700">{rule.preferred_action}</p>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${rule.status === "approved" ? "bg-green-100 text-green-700" : rule.status === "stale" ? "bg-slate-100 text-slate-500" : "bg-amber-100 text-amber-700"}`}>
                        {rule.status}
                      </span>
                    </div>
                    <p className="text-[10px] text-blue-500">{rule.trigger_pattern} | conf: {rule.confidence}% | reinforced: {rule.reinforcement_count}x</p>
                    {rule.status === "suggested" && (
                      <div className="flex gap-1 mt-1">
                        <Button size="sm" variant="ghost" className="text-[10px] h-6 px-2 text-green-600" onClick={() => handleRuleAction(rule.rule_id, "approve")}>Approve</Button>
                        <Button size="sm" variant="ghost" className="text-[10px] h-6 px-2 text-red-500" onClick={() => handleRuleAction(rule.rule_id, "retire")}>Retire</Button>
                        <Button size="sm" variant="ghost" className="text-[10px] h-6 px-2 text-purple-500" onClick={() => handleRuleAction(rule.rule_id, "demote_to_founder_dna")}>→ Founder</Button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Playbook Candidates */}
          {judgement.playbook_candidates?.length > 0 && (
            <div className="bg-white border border-emerald-200 rounded-lg p-4" data-testid="playbook-candidates">
              <h3 className="text-sm font-semibold text-emerald-800 mb-2">Playbook Candidates ({judgement.playbook_count})</h3>
              {judgement.playbook_candidates.map(pb => (
                <div key={pb.playbook_id} className="border-b border-emerald-100 py-2 last:border-0">
                  <p className="text-xs text-emerald-700">{pb.recommended_play}</p>
                  <p className="text-[10px] text-emerald-500">{pb.trigger_pattern} | {pb.dna_type?.replace(/_/g, " ")} | {pb.based_on_examples} examples | conf: {pb.confidence}%</p>
                </div>
              ))}
            </div>
          )}

          {/* Judgement Drifts */}
          {judgement.judgement_drifts?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid="judgement-drifts">
              <p className="text-xs font-bold text-red-700 mb-2">Judgement Drift Alerts</p>
              {judgement.judgement_drifts.map(d => (
                <p key={d.drift_id} className="text-xs text-red-600">{d.detail}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══════════ ACCOUNT INTELLIGENCE TAB ═══════════ */}
      {activeTab === "accounts" && accountIntel && (
        <div className="space-y-3" data-testid="account-intelligence">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <span>{accountIntel.total_accounts} accounts</span>
              <span>{accountIntel.total_paths} paths</span>
              <span>{accountIntel.total_vectors} vectors</span>
              <span className={accountIntel.high_risk_count > 0 ? "text-red-600 font-medium" : ""}>{accountIntel.total_risks} risks ({accountIntel.high_risk_count} high)</span>
            </div>
            <Button size="sm" variant="outline" onClick={async () => {
              await fetch(`${API}/mission-control/accounts/build`, { method: "POST", credentials: "include" });
              toast.success("Account intelligence rebuilt"); fetchAll();
            }} data-testid="build-accounts-btn">
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Rebuild
            </Button>
          </div>

          {accountIntel.accounts?.map(acct => (
            <div key={acct.account_id} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`account-card-${acct.account_id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                      acct.health_score >= 70 ? "bg-emerald-500" : acct.health_score >= 45 ? "bg-amber-500" : "bg-red-500"
                    }`}>{acct.health_score}</div>
                    <span className="text-sm font-semibold text-slate-900">{acct.company_name}</span>
                    <span className="text-[10px] text-slate-400">{acct.sector} | {acct.country}</span>
                    {acct.expansion_readiness && <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-300">Expansion Ready</Badge>}
                    {acct.churn_risk && <Badge variant="outline" className="text-[10px] bg-red-50 text-red-600 border-red-300">Churn Risk</Badge>}
                  </div>

                  {/* Stakeholder summary */}
                  <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500">
                    <span>{acct.stakeholder_count} stakeholders</span>
                    <span className="text-blue-600">{acct.revenue_nodes} revenue</span>
                    <span className="text-purple-600">{acct.influence_nodes} influence</span>
                    <span>warmth: <span className={acct.avg_warmth >= 60 ? "text-emerald-600" : acct.avg_warmth >= 30 ? "text-amber-600" : "text-red-600"}>{acct.avg_warmth}%</span></span>
                    {acct.founder_dependency_score > 50 && <span className="text-orange-600">founder dep: {acct.founder_dependency_score}%</span>}
                    {acct.dormant_champions > 0 && <span className="text-purple-600">{acct.dormant_champions} dormant champion(s)</span>}
                  </div>

                  {/* Relationship paths */}
                  {acct.relationship_paths?.length > 0 && (
                    <div className="mt-2 space-y-0.5">
                      {acct.relationship_paths.slice(0, 2).map(p => (
                        <p key={p.path_id} className="text-[10px] text-indigo-600">
                          Path: {p.from_node} → {p.to_node} ({p.path_type?.replace(/_/g, " ")}) — conf: {p.confidence}%
                        </p>
                      ))}
                    </div>
                  )}

                  {/* Expansion vectors */}
                  {acct.expansion_vectors?.length > 0 && (
                    <div className="mt-1 space-y-0.5">
                      {acct.expansion_vectors.slice(0, 2).map(v => (
                        <p key={v.vector_id} className="text-[10px] text-emerald-600">
                          Expand: {v.current_product} → {v.expansion_product} | window: {v.best_move_window_days}d {v.window_open ? "(open)" : "(not yet)"}
                        </p>
                      ))}
                    </div>
                  )}

                  {/* Risks */}
                  {acct.risks?.length > 0 && (
                    <div className="mt-1 space-y-0.5">
                      {acct.risks.slice(0, 2).map(r => (
                        <p key={r.risk_id} className={`text-[10px] ${r.severity === "high" ? "text-red-600" : "text-amber-600"}`}>
                          [{r.severity}] {r.detail}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══════════ GOVERNANCE TAB ═══════════ */}
      {activeTab === "governance" && governance && (
        <div className="space-y-4" data-testid="governance-panel">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">System health, adoption, trust, shadow workflows, governance journal.</p>
            <Button size="sm" variant="outline" onClick={async () => {
              await fetch(`${API}/mission-control/governance/check`, { method: "POST", credentials: "include" });
              toast.success("Governance checks run"); fetchAll();
            }} data-testid="run-governance-btn"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Run Checks</Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white border border-slate-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-slate-700">{governance.reliability?.count || 0}</p>
              <p className="text-xs text-slate-500">Reliability Events</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-amber-600">{governance.shadow_workflows?.count || 0}</p>
              <p className="text-xs text-slate-500">Shadow Workflows</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-red-600">{governance.trust_drifts?.count || 0}</p>
              <p className="text-xs text-slate-500">Trust Drifts</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-blue-600">{governance.adoption?.total_events || 0}</p>
              <p className="text-xs text-slate-500">Usage Events (7d)</p>
            </div>
          </div>

          {/* Ritual Compliance */}
          {governance.adoption?.ritual_compliance && (
            <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="ritual-compliance">
              <h3 className="text-sm font-semibold text-slate-800 mb-2">Ritual Compliance</h3>
              <div className="space-y-1.5">
                {governance.adoption.ritual_compliance.map((r, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${r.compliant ? "bg-green-500" : "bg-red-500"}`} />
                    <span className="text-xs text-slate-700 flex-1">{r.description}</span>
                    <span className={`text-[10px] font-medium ${r.compliant ? "text-green-600" : "text-red-600"}`}>
                      {r.compliant ? "Compliant" : "Breach"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Shadow Workflows */}
          {governance.shadow_workflows?.count > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="shadow-workflows">
              <h3 className="text-sm font-semibold text-amber-800 mb-2">Shadow Workflow Detections</h3>
              {governance.shadow_workflows.items.map((s, i) => (
                <div key={i} className="py-1.5 border-b border-amber-100 last:border-0">
                  <p className="text-xs text-amber-700">[{s.severity}] {s.detail}</p>
                </div>
              ))}
            </div>
          )}

          {/* Trust Drifts */}
          {governance.trust_drifts?.count > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="trust-drifts">
              <h3 className="text-sm font-semibold text-red-800 mb-2">Trust Drift Alerts</h3>
              {governance.trust_drifts.items.map((d, i) => (
                <div key={i} className="py-1.5 border-b border-red-100 last:border-0">
                  <p className="text-xs text-red-700">[{d.severity}] {d.detail}</p>
                </div>
              ))}
            </div>
          )}

          {/* Governance Journal */}
          {governance.governance_journal?.count > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-slate-800 mb-2">Governance Journal</h3>
              {governance.governance_journal.entries.slice(0, 5).map((e, i) => (
                <div key={i} className="py-1.5 border-b border-slate-100 last:border-0 text-xs text-slate-600">
                  <span className="font-medium">{e.field}</span>: {String(e.before)} → {String(e.after)} <span className="text-slate-400">by {e.changed_by}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══════════ BOARD NARRATIVE TAB ═══════════ */}
      {activeTab === "board" && boardNarrative && (
        <div className="space-y-4" data-testid="board-narrative">
          {/* Revenue Attribution */}
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Revenue Attribution</h3>
            <p className="text-3xl font-bold text-emerald-600">£{boardNarrative.revenue?.total_attributed?.toLocaleString()}</p>
            <div className="grid grid-cols-4 gap-3 mt-4">
              {Object.entries(boardNarrative.revenue?.confidence_tiers || {}).map(([tier, val]) => (
                <div key={tier} className="text-center">
                  <p className="text-lg font-bold text-slate-700">£{val.toLocaleString()}</p>
                  <p className="text-[10px] text-slate-500">{tier.replace(/_/g, " ")}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Key Metrics Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-blue-700">{boardNarrative.founder_leverage?.total_hours_saved?.toFixed(0)}</p>
              <p className="text-xs text-blue-600">Founder Hours Saved</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-amber-700">£{boardNarrative.risk_prevented?.total_risk_value_prevented?.toLocaleString()}</p>
              <p className="text-xs text-amber-600">Risk Value Prevented</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-red-700">£{boardNarrative.counterfactual?.total_counterfactual_loss?.toLocaleString()}</p>
              <p className="text-xs text-red-600">Likely Lost Without Rex</p>
            </div>
          </div>

          {/* Top Wins + Counterfactual */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-emerald-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-emerald-800 mb-2">Top Strategic Wins</h3>
              {boardNarrative.top_strategic_wins?.map((w, i) => (
                <p key={i} className="text-xs text-emerald-700 py-1 border-b border-emerald-100 last:border-0">{w}</p>
              ))}
            </div>
            <div className="bg-white border border-red-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-red-800 mb-2">Without Rex (Counterfactual)</h3>
              {boardNarrative.counterfactual?.counterfactuals?.map((c, i) => (
                <div key={i} className="py-1.5 border-b border-red-100 last:border-0">
                  <p className="text-xs text-red-700">£{c.estimated_loss?.toLocaleString()} — {c.category}</p>
                  <p className="text-[10px] text-red-500">{c.reasoning}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Productisation Readiness */}
          <div className="bg-white border border-purple-200 rounded-lg p-4" data-testid="productisation-readiness">
            <h3 className="text-sm font-semibold text-purple-800 mb-2">
              Productisation Readiness: {boardNarrative.productisation?.overall_score}%
              <span className="text-xs font-normal text-purple-500 ml-2">Most exportable: {boardNarrative.productisation?.most_exportable_label}</span>
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(boardNarrative.productisation?.modules || {}).map(([key, mod]) => (
                <div key={key} className="text-center p-2 rounded bg-purple-50">
                  <p className={`text-lg font-bold ${mod.score >= 80 ? "text-emerald-600" : mod.score >= 50 ? "text-amber-600" : "text-slate-500"}`}>{mod.score}%</p>
                  <p className="text-[10px] text-purple-600">{mod.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ LEARNING TAB ═══════════ */}
      {activeTab === "learning" && learning && (
        <div className="space-y-4" data-testid="learning-panel">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white border border-slate-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-slate-700">{learning.stats.total_drafts}</p>
              <p className="text-xs text-slate-500">Total Drafts</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-green-600">{learning.stats.approved}</p>
              <p className="text-xs text-slate-500">Approved</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-red-600">{learning.stats.rejected}</p>
              <p className="text-xs text-slate-500">Rejected</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-blue-600">{learning.stats.approval_rate}%</p>
              <p className="text-xs text-slate-500">Approval Rate</p>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg">
            <div className="p-3 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700">Recent Events</h3>
            </div>
            <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
              {learning.events.length === 0 ? (
                <p className="p-4 text-sm text-slate-400 text-center">No learning events yet</p>
              ) : (
                learning.events.map(evt => (
                  <div key={evt.event_id} className="px-4 py-2.5 flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      evt.event_type === "approval" ? "bg-green-500" :
                      evt.event_type === "rejection" ? "bg-red-500" :
                      evt.event_type === "edit" ? "bg-blue-500" : "bg-slate-400"
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-700 capitalize">{evt.event_type.replace(/_/g, " ")}</p>
                      {evt.details?.reason && <p className="text-xs text-slate-500">Reason: {evt.details.reason}</p>}
                      {evt.details?.subject && <p className="text-xs text-slate-500">Subject: {evt.details.subject}</p>}
                    </div>
                    <span className="text-xs text-slate-400 flex-shrink-0">
                      {new Date(evt.created_at).toLocaleDateString()}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ MODALS ═══════════ */}

      {/* Create Signal Modal */}
      <Dialog open={showCreateSignal} onOpenChange={setShowCreateSignal}>
        <DialogContent className="max-w-md" data-testid="create-signal-modal">
          <DialogHeader>
            <DialogTitle>New Market Signal</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Title</label>
              <Input
                value={newSignal.title}
                onChange={e => setNewSignal(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g., Nigeria Electricity Act 2024 Amendment"
                data-testid="signal-title-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Summary</label>
              <Textarea
                value={newSignal.summary}
                onChange={e => setNewSignal(p => ({ ...p, summary: e.target.value }))}
                placeholder="Describe the regulatory change and its commercial implications..."
                rows={4}
                data-testid="signal-summary-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Source Name</label>
                <Input
                  value={newSignal.source_name}
                  onChange={e => setNewSignal(p => ({ ...p, source_name: e.target.value }))}
                  placeholder="e.g., BPE"
                  data-testid="signal-source-name-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Geography</label>
                <Select value={newSignal.geography} onValueChange={v => setNewSignal(p => ({ ...p, geography: v }))}>
                  <SelectTrigger data-testid="signal-geography-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Nigeria">Nigeria</SelectItem>
                    <SelectItem value="Ghana">Ghana</SelectItem>
                    <SelectItem value="Kenya">Kenya</SelectItem>
                    <SelectItem value="Africa">Pan-Africa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Source URL</label>
              <Input
                value={newSignal.source_url}
                onChange={e => setNewSignal(p => ({ ...p, source_url: e.target.value }))}
                placeholder="https://..."
                data-testid="signal-source-url-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateSignal(false)}>Cancel</Button>
            <Button onClick={handleCreateSignal} disabled={!newSignal.title || !newSignal.summary} data-testid="submit-signal-btn">
              Create Signal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* #2 — Assign a signal to a colleague: the SAME hand-off (real-staff picker, brief,
          accept/decline). The signal is marked handed_off so it leaves the actionable set
          and shows "Assigned to X" — it neither vanishes silently nor looks untouched. */}
      <AssignWithBriefModal
        open={!!assignSignal}
        onClose={() => setAssignSignal(null)}
        title="Assign to…"
        subtitle={assignSignal?.title || ""}
        assignablePool={staffPool}
        currentUserEmail={user?.email}
        pickerHint="A colleague picks it up on their Today list and accepts or declines it."
        briefLabel="Brief for them"
        briefHint="Optional — what to do with it, so they don't have to come back and ask."
        confirmLabel="Assign to person"
        onConfirm={async (vals) => {
          const ok = await assignSignalToPerson(vals);
          return ok !== false;
        }}
      />

      {/* Steering Modal */}
      <Dialog open={showSteeringModal} onOpenChange={setShowSteeringModal}>
        <DialogContent className="max-w-md" data-testid="steering-modal">
          <DialogHeader>
            <DialogTitle>Capture Steering</DialogTitle>
          </DialogHeader>
          {selectedSignal && (
            <div className="space-y-3">
              <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
                <p className="font-semibold text-slate-800 mb-1">{selectedSignal.title}</p>
                <p className="line-clamp-2">{selectedSignal.summary}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Target Seniority</label>
                <Select value={steering.target_seniority} onValueChange={v => setSteering(p => ({ ...p, target_seniority: v }))}>
                  <SelectTrigger data-testid="steering-seniority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="C-Suite">C-Suite (CEO, CFO, COO)</SelectItem>
                    <SelectItem value="VP/Director">VP / Director</SelectItem>
                    <SelectItem value="Head of Department">Head of Department</SelectItem>
                    <SelectItem value="Board Level">Board Level</SelectItem>
                    <SelectItem value="HR/People Leaders">HR / People Leaders</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Tone</label>
                <Select value={steering.tone} onValueChange={v => setSteering(p => ({ ...p, tone: v }))}>
                  <SelectTrigger data-testid="steering-tone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="authoritative">Authoritative</SelectItem>
                    <SelectItem value="consultative">Consultative</SelectItem>
                    <SelectItem value="provocative">Provocative</SelectItem>
                    <SelectItem value="empathetic">Empathetic</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Angle / Hook</label>
                <Input
                  value={steering.angle}
                  onChange={e => setSteering(p => ({ ...p, angle: e.target.value }))}
                  placeholder="e.g., Leadership readiness for regulatory change"
                  data-testid="steering-angle-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Urgency</label>
                <Select value={steering.urgency} onValueChange={v => setSteering(p => ({ ...p, urgency: v }))}>
                  <SelectTrigger data-testid="steering-urgency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Target Companies (comma-separated)</label>
                <Input
                  value={steering.target_companies}
                  onChange={e => setSteering(p => ({ ...p, target_companies: e.target.value }))}
                  placeholder="e.g., Dangote Energy, NLNG, Oando"
                  data-testid="steering-companies-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Notes</label>
                <Textarea
                  value={steering.notes}
                  onChange={e => setSteering(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Any additional context for Rex..."
                  rows={2}
                  data-testid="steering-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSteeringModal(false)}>Cancel</Button>
            <Button onClick={handleSubmitSteering} data-testid="submit-steering-btn">
              <Target className="h-4 w-4 mr-1" /> Save Steering
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Detail Modal */}
      <Dialog open={showCampaignDetail} onOpenChange={setShowCampaignDetail}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="campaign-detail-modal">
          <DialogHeader>
            <DialogTitle>Campaign Draft</DialogTitle>
          </DialogHeader>
          {selectedCampaign && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <StatusBadge status={selectedCampaign.status} />
                {selectedCampaign.sender_name && <span className="text-xs text-slate-500">via {selectedCampaign.sender_name}</span>}
              </div>

              <div>
                <label className="text-xs font-medium text-slate-500">Subject</label>
                <p className="text-sm font-semibold text-slate-900">{selectedCampaign.subject}</p>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-500">Email Body</label>
                <div
                  className="mt-1 bg-white border border-slate-200 rounded-lg p-4 text-sm text-slate-700 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: selectedCampaign.body_html }}
                  data-testid="campaign-body-preview"
                />
              </div>

              {selectedCampaign.talking_points?.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-slate-500">Talking Points</label>
                  <ul className="mt-1 space-y-1">
                    {selectedCampaign.talking_points.map((pt, i) => (
                      <li key={i} className="text-xs text-slate-600 flex items-start gap-2">
                        <span className="text-blue-500 mt-0.5">-</span>
                        {pt}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedCampaign.rejected_reason && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-red-700">Rejection Reason</p>
                  <p className="text-sm text-red-600">{selectedCampaign.rejected_reason}</p>
                </div>
              )}

              {selectedCampaign.recipients?.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-slate-500">Recipients</label>
                  <div className="mt-1 space-y-1">
                    {selectedCampaign.recipients.map((r, i) => (
                      <p key={i} className="text-xs text-slate-600">{r.name} — {r.email} {r.company ? `(${r.company})` : ""}</p>
                    ))}
                  </div>
                </div>
              )}

              {selectedCampaign.status === "draft" && (
                <div className="flex gap-2 pt-2 border-t border-slate-100">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditForm({ subject: selectedCampaign.subject, body_html: selectedCampaign.body_html });
                      setShowEditModal(true);
                    }}
                    data-testid="edit-campaign-btn"
                  >
                    <Edit3 className="h-3.5 w-3.5 mr-1" /> Edit
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      setApprovalForm({ sender_id: "", recipients: "" });
                      setShowApproveModal(true);
                    }}
                    disabled={activeSenders.length === 0}
                    data-testid="approve-campaign-btn"
                  >
                    <Check className="h-3.5 w-3.5 mr-1" /> Approve & Send
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => { setRejectReason(""); setShowRejectModal(true); }}
                    data-testid="reject-campaign-btn"
                  >
                    <X className="h-3.5 w-3.5 mr-1" /> Reject
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approve Modal */}
      <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
        <DialogContent className="max-w-md" data-testid="approve-modal">
          <DialogHeader>
            <DialogTitle>Approve & Send Campaign</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Select Sender</label>
              <Select value={approvalForm.sender_id} onValueChange={v => setApprovalForm(p => ({ ...p, sender_id: v }))}>
                <SelectTrigger data-testid="approve-sender-select">
                  <SelectValue placeholder="Choose sender identity..." />
                </SelectTrigger>
                <SelectContent>
                  {activeSenders.map(s => (
                    <SelectItem key={s.sender_id} value={s.sender_id}>
                      {s.display_name} ({s.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Recipients (one per line: Name, Email, Company)</label>
              <Textarea
                value={approvalForm.recipients}
                onChange={e => setApprovalForm(p => ({ ...p, recipients: e.target.value }))}
                placeholder={"John Doe, john@company.com, Dangote Energy\nJane Smith, jane@company.com, NLNG"}
                rows={5}
                data-testid="approve-recipients-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveModal(false)}>Cancel</Button>
            <Button
              onClick={handleApproveCampaign}
              disabled={!approvalForm.sender_id || !approvalForm.recipients.trim()}
              data-testid="confirm-approve-btn"
            >
              <Send className="h-4 w-4 mr-1" /> Approve & Enqueue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="max-w-md" data-testid="reject-modal">
          <DialogHeader>
            <DialogTitle>Reject Campaign</DialogTitle>
          </DialogHeader>
          <div>
            <label className="text-xs font-medium text-slate-600">Reason for rejection</label>
            <Textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Explain why this draft needs rework..."
              rows={3}
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectModal(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleRejectCampaign}
              disabled={!rejectReason.trim()}
              data-testid="confirm-reject-btn"
            >
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Campaign Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl" data-testid="edit-campaign-modal">
          <DialogHeader>
            <DialogTitle>Edit Campaign Draft</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Subject</label>
              <Input
                value={editForm.subject}
                onChange={e => setEditForm(p => ({ ...p, subject: e.target.value }))}
                data-testid="edit-subject-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Body (HTML)</label>
              <Textarea
                value={editForm.body_html}
                onChange={e => setEditForm(p => ({ ...p, body_html: e.target.value }))}
                rows={12}
                className="font-mono text-xs"
                data-testid="edit-body-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleEditCampaign} data-testid="save-edit-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Sender Modal */}
      <Dialog open={showAddSender} onOpenChange={setShowAddSender}>
        <DialogContent className="max-w-md" data-testid="add-sender-modal">
          <DialogHeader>
            <DialogTitle>Add Authorized Sender</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Display Name</label>
              <Input
                value={newSender.display_name}
                onChange={e => setNewSender(p => ({ ...p, display_name: e.target.value }))}
                placeholder="e.g., Uchenna Okoro"
                data-testid="sender-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Email</label>
              <Input
                value={newSender.email}
                onChange={e => setNewSender(p => ({ ...p, email: e.target.value }))}
                placeholder="e.g., uchenna@resiligent.com"
                data-testid="sender-email-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Title</label>
              <Input
                value={newSender.title}
                onChange={e => setNewSender(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g., Managing Partner"
                data-testid="sender-title-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSender(false)}>Cancel</Button>
            <Button
              onClick={handleAddSender}
              disabled={!newSender.email || !newSender.display_name}
              data-testid="submit-sender-btn"
            >
              Add Sender
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══════════ DORMANT MODALS ═══════════ */}

      {/* Revival Steering Modal */}
      <Dialog open={showRevivalSteering} onOpenChange={setShowRevivalSteering}>
        <DialogContent className="max-w-md" data-testid="revival-steering-modal">
          <DialogHeader>
            <DialogTitle>Revival Steering — {selectedDormant?.company_name}</DialogTitle>
          </DialogHeader>
          {selectedDormant && (
            <div className="space-y-3">
              <div className="bg-amber-50 rounded-lg p-3 text-xs text-amber-800">
                <p className="font-semibold">Confidence: {selectedDormant.revive_confidence}% | {selectedDormant.days_dormant} days dormant</p>
                <p className="mt-1">{selectedDormant.suggested_angle}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Target Seniority</label>
                <Select value={revivalSteering.target_seniority} onValueChange={v => setRevivalSteering(p => ({ ...p, target_seniority: v }))}>
                  <SelectTrigger data-testid="revival-seniority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="C-Suite">C-Suite (CEO, CFO, COO)</SelectItem>
                    <SelectItem value="VP/Director">VP / Director</SelectItem>
                    <SelectItem value="Head of Department">Head of Department</SelectItem>
                    <SelectItem value="Board Level">Board Level</SelectItem>
                    <SelectItem value="HR/People Leaders">HR / People Leaders</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Tone</label>
                <Select value={revivalSteering.tone} onValueChange={v => setRevivalSteering(p => ({ ...p, tone: v }))}>
                  <SelectTrigger data-testid="revival-tone-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="consultative">Consultative</SelectItem>
                    <SelectItem value="authoritative">Authoritative</SelectItem>
                    <SelectItem value="empathetic">Empathetic</SelectItem>
                    <SelectItem value="provocative">Provocative</SelectItem>
                    <SelectItem value="warm">Warm & Personal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Re-entry Angle</label>
                <Input value={revivalSteering.angle} onChange={e => setRevivalSteering(p => ({ ...p, angle: e.target.value }))}
                  placeholder="e.g., Reference recent regulatory change as fresh context" data-testid="revival-angle-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Urgency</label>
                <Select value={revivalSteering.urgency} onValueChange={v => setRevivalSteering(p => ({ ...p, urgency: v }))}>
                  <SelectTrigger data-testid="revival-urgency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={revivalSteering.reference_proposal}
                  onChange={e => setRevivalSteering(p => ({ ...p, reference_proposal: e.target.checked }))}
                  id="ref-proposal" className="rounded" data-testid="revival-ref-proposal" />
                <label htmlFor="ref-proposal" className="text-xs text-slate-600">Reference prior proposals</label>
              </div>
              {signals.filter(s => s.status === "reviewed" || s.status === "new").length > 0 && (
                <div>
                  <label className="text-xs font-medium text-slate-600">Overlay Market Signal (optional)</label>
                  <Select value={revivalSteering.overlay_signal_id || "none"} onValueChange={v => setRevivalSteering(p => ({ ...p, overlay_signal_id: v === "none" ? "" : v }))}>
                    <SelectTrigger data-testid="revival-signal-overlay"><SelectValue placeholder="No overlay" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No overlay</SelectItem>
                      {signals.filter(s => ["new", "reviewed", "campaign_ready"].includes(s.status)).map(s => (
                        <SelectItem key={s.signal_id} value={s.signal_id}>{s.title.substring(0, 60)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-slate-600">Notes</label>
                <Textarea value={revivalSteering.notes} onChange={e => setRevivalSteering(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Extra context for Rex..." rows={2} data-testid="revival-notes-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRevivalSteering(false)}>Cancel</Button>
            <Button onClick={handleBuildRevival} disabled={buildingRevival} data-testid="build-revival-btn">
              {buildingRevival ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
              Generate Revival Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dormant Action Modal (Hold / Dismiss / Founder Only) */}
      <Dialog open={!!showDormantAction} onOpenChange={() => setShowDormantAction(null)}>
        <DialogContent className="max-w-sm" data-testid="dormant-action-modal">
          <DialogHeader>
            <DialogTitle>
              {showDormantAction === "hold" ? "Hold" : showDormantAction === "dismiss" ? "Dismiss" : "Flag Founder Only"} — {selectedDormant?.company_name}
            </DialogTitle>
          </DialogHeader>
          <div>
            <label className="text-xs font-medium text-slate-600">Reason (optional)</label>
            <Textarea value={dormantActionReason} onChange={e => setDormantActionReason(e.target.value)}
              placeholder="Why are you taking this action?" rows={2} data-testid="dormant-action-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDormantAction(null)}>Cancel</Button>
            <Button onClick={() => handleDormantAction(selectedDormant?.client_id, showDormantAction === "founder_only" ? "founder-only" : showDormantAction, dormantActionReason)}
              data-testid="confirm-dormant-action-btn">
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Content Bridge Modal */}
      <Dialog open={showContentBridge} onOpenChange={setShowContentBridge}>
        <DialogContent className="max-w-md" data-testid="content-bridge-modal">
          <DialogHeader>
            <DialogTitle>Convert to Content Touchpoint</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Suggested Angle</label>
              <Input value={contentBridgeForm.suggested_angle} onChange={e => setContentBridgeForm(p => ({ ...p, suggested_angle: e.target.value }))}
                data-testid="bridge-angle-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Publish Intent</label>
              <Select value={contentBridgeForm.publish_intent} onValueChange={v => setContentBridgeForm(p => ({ ...p, publish_intent: v }))}>
                <SelectTrigger data-testid="bridge-intent-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="linkedin_post">LinkedIn Post</SelectItem>
                  <SelectItem value="case_study">Case Study</SelectItem>
                  <SelectItem value="newsletter">Newsletter</SelectItem>
                  <SelectItem value="thought_leadership">Thought Leadership</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Target Audience</label>
              <Input value={contentBridgeForm.target_audience} onChange={e => setContentBridgeForm(p => ({ ...p, target_audience: e.target.value }))}
                placeholder="e.g., Energy sector C-Suite in Nigeria" data-testid="bridge-audience-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Reason</label>
              <Textarea value={contentBridgeForm.reason} onChange={e => setContentBridgeForm(p => ({ ...p, reason: e.target.value }))}
                placeholder="Why content is better than direct outreach here..." rows={2} data-testid="bridge-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContentBridge(false)}>Cancel</Button>
            <Button onClick={handleContentBridge} data-testid="submit-bridge-btn">
              <Palette className="h-4 w-4 mr-1" /> Create Bridge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Outcome Recording Modal */}
      <Dialog open={showOutcomeModal} onOpenChange={setShowOutcomeModal}>
        <DialogContent className="max-w-sm" data-testid="outcome-modal">
          <DialogHeader>
            <DialogTitle>Record Outcome — {selectedDormant?.company_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: "revived", label: "Revived", color: "bg-green-100 text-green-700 border-green-300" },
                { value: "responded", label: "Responded", color: "bg-blue-100 text-blue-700 border-blue-300" },
                { value: "meeting_booked", label: "Meeting Booked", color: "bg-emerald-100 text-emerald-700 border-emerald-300" },
                { value: "proposal_reopened", label: "Proposal Reopened", color: "bg-purple-100 text-purple-700 border-purple-300" },
                { value: "no_response", label: "No Response", color: "bg-slate-100 text-slate-600 border-slate-300" },
              ].map(o => (
                <button key={o.value}
                  onClick={() => setOutcomeForm(p => ({ ...p, outcome: o.value }))}
                  className={`px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                    outcomeForm.outcome === o.value ? o.color + " ring-2 ring-offset-1" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`} data-testid={`outcome-option-${o.value}`}>
                  {o.label}
                </button>
              ))}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Notes</label>
              <Textarea value={outcomeForm.notes} onChange={e => setOutcomeForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="Optional notes..." rows={2} data-testid="outcome-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOutcomeModal(false)}>Cancel</Button>
            <Button onClick={handleRecordOutcome} disabled={!outcomeForm.outcome} data-testid="submit-outcome-btn">
              Record Outcome
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cooling Config Modal */}
      <Dialog open={showCoolingConfig} onOpenChange={setShowCoolingConfig}>
        <DialogContent className="max-w-sm" data-testid="cooling-config-modal">
          <DialogHeader>
            <DialogTitle>Cooling-Off Window</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Dismissed or held dormant deals won't resurface for this many days.</p>
            <div>
              <label className="text-xs font-medium text-slate-600">Days</label>
              <Input type="number" value={coolingDays} onChange={e => setCoolingDays(parseInt(e.target.value) || 14)}
                min={1} max={90} data-testid="cooling-days-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCoolingConfig(false)}>Cancel</Button>
            <Button onClick={handleUpdateCooling} data-testid="save-cooling-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══════════ SEQUENCE MODALS ═══════════ */}

      {/* Sequence Detail Modal */}
      <Dialog open={showSequenceDetail} onOpenChange={setShowSequenceDetail}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="sequence-detail-modal">
          <DialogHeader>
            <DialogTitle>Sequence Play</DialogTitle>
          </DialogHeader>
          {selectedSequence && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={selectedSequence.status} />
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${selectedSequence.confidence >= 70 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {selectedSequence.confidence}%
                </span>
                <span className="text-xs text-slate-500">{selectedSequence.pattern?.replace(/_/g, " ")}</span>
              </div>

              <div>
                <p className="text-sm font-semibold text-slate-900">{selectedSequence.expected_conversion}</p>
                <p className="text-xs text-slate-600 mt-1">Sender: {selectedSequence.sender || "Not assigned"}</p>
              </div>

              {/* Rationale */}
              <div className="bg-indigo-50 rounded-lg p-3" data-testid="sequence-rationale">
                <p className="text-xs font-medium text-indigo-700">Why this sequence should work</p>
                <p className="text-xs text-indigo-600 mt-1">{selectedSequence.rationale}</p>
              </div>

              {/* Autopilot Safety Rationale */}
              {selectedSequence.status === "approved" && autopilotStatus?.armed_sequences && (() => {
                const armed = autopilotStatus.armed_sequences.find(a => a.sequence_id === selectedSequence.sequence_id);
                if (!armed) return null;
                return (
                  <div className={`rounded-lg p-3 ${armed.eligible ? "bg-emerald-50 border border-emerald-200" : "bg-amber-50 border border-amber-200"}`}
                    data-testid="autopilot-detail">
                    <p className={`text-xs font-medium ${armed.eligible ? "text-emerald-700" : "text-amber-700"}`}>
                      {armed.eligible ? "Why autopilot is safe" : "Autopilot blocked"}
                    </p>
                    <div className="mt-1 space-y-0.5">
                      {armed.safety_rationale?.map((r, i) => (
                        <p key={i} className={`text-[10px] ${armed.eligible ? "text-emerald-600" : "text-amber-600"}`}>
                          {armed.eligible ? "✓" : "•"} {r}
                        </p>
                      ))}
                      {!armed.eligible && armed.reason && (
                        <p className="text-[10px] text-red-600 font-medium mt-1">Block: {armed.reason}</p>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Step Timeline */}
              <div className="space-y-2" data-testid="sequence-steps">
                {selectedSequence.steps?.map((step, i) => (
                  <div key={i} className={`border rounded-lg p-3 ${
                    step.status === "completed" ? "border-green-300 bg-green-50" :
                    step.status === "skipped" || step.status === "aborted" ? "border-slate-200 bg-slate-50 opacity-50" :
                    "border-slate-200 bg-white"
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          step.status === "completed" ? "bg-green-500 text-white" :
                          step.status === "skipped" ? "bg-slate-300 text-white" :
                          "bg-blue-100 text-blue-700"
                        }`}>{step.step_number}</div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{step.action?.replace(/_/g, " ")}</p>
                          <p className="text-[10px] text-slate-500">{step.item_label} | {step.loop?.replace(/_/g, " ")} | +{step.delay_hours}h</p>
                        </div>
                      </div>
                      {step.status === "pending" && selectedSequence.status === "approved" && (
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => handleExecuteStep(selectedSequence.sequence_id, step.step_number)}>
                            <Check className="h-3 w-3 mr-1" /> Done
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleSkipStep(selectedSequence.sequence_id, step.step_number)}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                      {step.status === "completed" && (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 text-[10px]">Done</Badge>
                      )}
                    </div>
                    {step.abort_conditions?.length > 0 && (
                      <div className="mt-1.5 flex gap-1 flex-wrap">
                        {step.abort_conditions.map((c, j) => (
                          <span key={j} className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-500 border border-red-200">
                            abort: {c.replace(/_/g, " ")}
                          </span>
                        ))}
                      </div>
                    )}
                    {step.scheduled_time && (
                      <p className="text-[10px] text-slate-400 mt-1">Scheduled: {new Date(step.scheduled_time).toLocaleString()}</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t border-slate-100">
                {selectedSequence.status === "proposed" && (
                  <Button size="sm" onClick={() => handleApproveSequence(selectedSequence.sequence_id)} data-testid="detail-approve-seq">
                    <Check className="h-3.5 w-3.5 mr-1" /> Approve Sequence
                  </Button>
                )}
                {selectedSequence.status === "approved" && (
                  <Button size="sm" variant="destructive" onClick={() => handleAbortSequence(selectedSequence.sequence_id)}>
                    <X className="h-3.5 w-3.5 mr-1" /> Abort (Early Success)
                  </Button>
                )}
                {(selectedSequence.status === "completed" || selectedSequence.status === "aborted") && !selectedSequence.outcome && (
                  <Button size="sm" variant="outline" onClick={() => { setSequenceOutcome({ result: "", notes: "", meeting_created: false, proposal_created: false }); setShowSequenceOutcome(true); }}>
                    <Award className="h-3.5 w-3.5 mr-1" /> Record Outcome
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sequence Outcome Modal */}
      <Dialog open={showSequenceOutcome} onOpenChange={setShowSequenceOutcome}>
        <DialogContent className="max-w-sm" data-testid="sequence-outcome-modal">
          <DialogHeader>
            <DialogTitle>Record Sequence Outcome</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              {["success", "partial", "failed", "aborted"].map(r => (
                <button key={r} onClick={() => setSequenceOutcome(p => ({ ...p, result: r }))}
                  className={`px-3 py-2 rounded-lg border text-xs font-medium ${
                    sequenceOutcome.result === r
                      ? r === "success" ? "bg-green-100 text-green-700 border-green-300 ring-2" :
                        r === "partial" ? "bg-amber-100 text-amber-700 border-amber-300 ring-2" :
                        "bg-red-100 text-red-700 border-red-300 ring-2"
                      : "bg-white border-slate-200 text-slate-600"
                  }`} data-testid={`seq-outcome-${r}`}>
                  {r}
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" className="rounded" checked={sequenceOutcome.meeting_created}
                  onChange={e => setSequenceOutcome(p => ({ ...p, meeting_created: e.target.checked }))} />
                Meeting created from sequence
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" className="rounded" checked={sequenceOutcome.proposal_created}
                  onChange={e => setSequenceOutcome(p => ({ ...p, proposal_created: e.target.checked }))} />
                Proposal created from sequence
              </label>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Notes</label>
              <Textarea value={sequenceOutcome.notes} onChange={e => setSequenceOutcome(p => ({ ...p, notes: e.target.value }))}
                placeholder="What happened?" rows={2} data-testid="seq-outcome-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSequenceOutcome(false)}>Cancel</Button>
            <Button onClick={handleSequenceOutcome} disabled={!sequenceOutcome.result} data-testid="submit-seq-outcome">Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══════════ FLYWHEEL MODALS ═══════════ */}

      {/* Create / Activate Flywheel Modal */}
      <Dialog open={showFlywheelCreate} onOpenChange={setShowFlywheelCreate}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="flywheel-create-modal">
          <DialogHeader>
            <DialogTitle>New Distribution Flywheel Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {pendingBridges.length > 0 && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <p className="text-xs font-medium text-purple-700 mb-2">Pending Content Bridges</p>
                {pendingBridges.map(b => (
                  <button key={b.bridge_id} onClick={() => {
                    setFlywheelForm(p => ({ ...p, context_title: b.company_name, context_summary: b.suggested_angle, target_audience: b.target_audience || "" }));
                  }} className="block w-full text-left text-xs text-purple-600 hover:bg-purple-100 px-2 py-1 rounded">
                    {b.company_name} — {b.suggested_angle?.substring(0, 60)}
                  </button>
                ))}
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-slate-600">Why are we publishing this? <span className="text-red-500">*</span></label>
              <Select value={flywheelForm.strategic_purpose} onValueChange={v => setFlywheelForm(p => ({ ...p, strategic_purpose: v }))}>
                <SelectTrigger data-testid="fw-purpose-select"><SelectValue placeholder="Select strategic purpose..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="market_authority">Market Authority</SelectItem>
                  <SelectItem value="client_nurture">Client Nurture</SelectItem>
                  <SelectItem value="congratulatory_touchpoint">Congratulatory Touchpoint</SelectItem>
                  <SelectItem value="product_awareness">Product Awareness</SelectItem>
                  <SelectItem value="proof_credibility">Proof / Credibility</SelectItem>
                  <SelectItem value="event_catalyst">Event Catalyst</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Content Type</label>
                <Select value={flywheelForm.content_type} onValueChange={v => setFlywheelForm(p => ({ ...p, content_type: v }))}>
                  <SelectTrigger data-testid="fw-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="linkedin_post">LinkedIn Post</SelectItem>
                    <SelectItem value="carousel_brief">Carousel Brief</SelectItem>
                    <SelectItem value="congratulatory_note">Congratulatory Note</SelectItem>
                    <SelectItem value="newsletter_snippet">Newsletter Snippet</SelectItem>
                    <SelectItem value="pov_article">POV Article</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Brand</label>
                <Select value={flywheelForm.brand_mode} onValueChange={v => setFlywheelForm(p => ({ ...p, brand_mode: v }))}>
                  <SelectTrigger data-testid="fw-brand-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="remi_only">Remi Only</SelectItem>
                    <SelectItem value="resiligent_only">RESILIGENT Only</SelectItem>
                    <SelectItem value="dual_brand">Dual Brand (Linked Sequence)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Linked Offering</label>
              <Select value={flywheelForm.linked_offering} onValueChange={v => setFlywheelForm(p => ({ ...p, linked_offering: v }))}>
                <SelectTrigger data-testid="fw-offering-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["None","CulturePulse","DISC Lens Africa","Leadership Mirror","Impact 360","InsightEdge","Executive Coaching","SheLeads","Leadership Retreats","Management Ownership Labs","Behavioural Ethics"].map(o => (
                    <SelectItem key={o} value={o}>{o}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Target Audience</label>
              <Input value={flywheelForm.target_audience} onChange={e => setFlywheelForm(p => ({ ...p, target_audience: e.target.value }))}
                placeholder="e.g., Nigerian energy sector C-Suite" data-testid="fw-audience-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Context Title</label>
              <Input value={flywheelForm.context_title} onChange={e => setFlywheelForm(p => ({ ...p, context_title: e.target.value }))}
                placeholder="What is this about?" data-testid="fw-title-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Context / Angle</label>
              <Textarea value={flywheelForm.context_summary} onChange={e => setFlywheelForm(p => ({ ...p, context_summary: e.target.value }))}
                placeholder="Key insight or angle..." rows={3} data-testid="fw-summary-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Timing</label>
              <Select value={flywheelForm.timing} onValueChange={v => setFlywheelForm(p => ({ ...p, timing: v }))}>
                <SelectTrigger data-testid="fw-timing-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="publish_now">Publish Now</SelectItem>
                  <SelectItem value="hold_for_later">Hold for Later</SelectItem>
                  <SelectItem value="tie_to_date">Tie to Date / Event</SelectItem>
                  <SelectItem value="revisit_next_week">Revisit Next Week</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {flywheelForm.timing === "tie_to_date" && (
              <div>
                <label className="text-xs font-medium text-slate-600">Scheduled Date</label>
                <Input type="date" value={flywheelForm.scheduled_date} onChange={e => setFlywheelForm(p => ({ ...p, scheduled_date: e.target.value }))}
                  data-testid="fw-date-input" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFlywheelCreate(false)}>Cancel</Button>
            <Button onClick={handleCreateFlywheel} disabled={!flywheelForm.strategic_purpose} data-testid="submit-flywheel-btn">
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signal to Content Modal */}
      <Dialog open={showFlywheelFromSignal} onOpenChange={setShowFlywheelFromSignal}>
        <DialogContent className="max-w-md" data-testid="signal-to-content-modal">
          <DialogHeader>
            <DialogTitle>Convert Signal to Content</DialogTitle>
          </DialogHeader>
          {selectedSignal && (
            <div className="space-y-3">
              <div className="bg-slate-50 rounded-lg p-3 text-xs">
                <p className="font-semibold text-slate-800">{selectedSignal.title}</p>
                <p className="text-slate-600 mt-1 line-clamp-2">{selectedSignal.summary}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Why are we publishing this? <span className="text-red-500">*</span></label>
                <Select value={flywheelForm.strategic_purpose} onValueChange={v => setFlywheelForm(p => ({ ...p, strategic_purpose: v }))}>
                  <SelectTrigger data-testid="sig-content-purpose"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="market_authority">Market Authority</SelectItem>
                    <SelectItem value="client_nurture">Client Nurture</SelectItem>
                    <SelectItem value="proof_credibility">Proof / Credibility</SelectItem>
                    <SelectItem value="event_catalyst">Event Catalyst</SelectItem>
                    <SelectItem value="product_awareness">Product Awareness</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Brand</label>
                  <Select value={flywheelForm.brand_mode} onValueChange={v => setFlywheelForm(p => ({ ...p, brand_mode: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="remi_only">Remi</SelectItem>
                      <SelectItem value="resiligent_only">RESILIGENT</SelectItem>
                      <SelectItem value="dual_brand">Dual Brand</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Type</label>
                  <Select value={flywheelForm.content_type} onValueChange={v => setFlywheelForm(p => ({ ...p, content_type: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="linkedin_post">LinkedIn Post</SelectItem>
                      <SelectItem value="carousel_brief">Carousel Brief</SelectItem>
                      <SelectItem value="pov_article">POV Article</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFlywheelFromSignal(false)}>Cancel</Button>
            <Button onClick={() => handleSignalToContent(selectedSignal?.signal_id)} disabled={!flywheelForm.strategic_purpose}
              data-testid="submit-signal-content-btn">
              <Megaphone className="h-4 w-4 mr-1" /> Convert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Flywheel Detail Modal */}
      <Dialog open={showFlywheelDetail} onOpenChange={setShowFlywheelDetail}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="flywheel-detail-modal">
          <DialogHeader>
            <DialogTitle>Distribution Flywheel Item</DialogTitle>
          </DialogHeader>
          {selectedFlywheel && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={selectedFlywheel.status} />
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  selectedFlywheel.brand_mode === "dual_brand" ? "bg-gradient-to-r from-blue-100 to-purple-100 text-purple-700" :
                  selectedFlywheel.brand_mode === "remi_only" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-700"
                }`}>{selectedFlywheel.brand_mode?.replace(/_/g, " ")}</span>
                <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded">{selectedFlywheel.strategic_purpose?.replace(/_/g, " ")}</span>
                {selectedFlywheel.designer_status !== "no_designer" && (
                  <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded">{selectedFlywheel.designer_status?.replace(/_/g, " ")}</span>
                )}
              </div>

              <div>
                <label className="text-xs font-medium text-slate-500">Context</label>
                <p className="text-sm font-semibold text-slate-900">{selectedFlywheel.context_title}</p>
                <p className="text-xs text-slate-600 mt-1">{selectedFlywheel.context_summary}</p>
              </div>

              {selectedFlywheel.primary_draft && (
                <div className="bg-white border border-blue-200 rounded-lg p-4">
                  <p className="text-xs font-medium text-blue-600 mb-2">
                    Primary — {selectedFlywheel.primary_draft.voice === "remi" ? "Remi" : "RESILIGENT"}
                  </p>
                  <p className="text-sm font-semibold text-slate-900 italic">"{selectedFlywheel.primary_draft.hook}"</p>
                  <p className="text-xs text-slate-700 mt-2 whitespace-pre-line">{selectedFlywheel.primary_draft.body}</p>
                  <p className="text-xs text-blue-600 mt-2 font-medium">CTA: {selectedFlywheel.primary_draft.cta}</p>
                  {selectedFlywheel.primary_draft.hashtags?.length > 0 && (
                    <p className="text-xs text-slate-400 mt-1">{selectedFlywheel.primary_draft.hashtags.join(" ")}</p>
                  )}
                  {selectedFlywheel.primary_draft.designer_brief && (
                    <div className="mt-2 bg-yellow-50 rounded p-2">
                      <p className="text-xs font-medium text-yellow-700">Designer Brief:</p>
                      <p className="text-xs text-yellow-600">{selectedFlywheel.primary_draft.designer_brief}</p>
                    </div>
                  )}
                </div>
              )}

              {selectedFlywheel.secondary_draft && (
                <div className="bg-white border border-purple-200 rounded-lg p-4">
                  <p className="text-xs font-medium text-purple-600 mb-2">
                    Secondary — {selectedFlywheel.secondary_draft.voice === "remi" ? "Remi" : "RESILIGENT"}
                  </p>
                  <p className="text-sm font-semibold text-slate-900 italic">"{selectedFlywheel.secondary_draft.hook}"</p>
                  <p className="text-xs text-slate-700 mt-2 whitespace-pre-line">{selectedFlywheel.secondary_draft.body}</p>
                </div>
              )}

              {selectedFlywheel.posting_order && (
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-slate-700">Posting Order: <span className="text-blue-600">{selectedFlywheel.posting_order?.replace(/_/g, " ")}</span></p>
                  {selectedFlywheel.amplification_prompt && (
                    <p className="text-xs text-slate-600 mt-1"><Share2 className="h-3 w-3 inline mr-1" />Team Amplification: {selectedFlywheel.amplification_prompt}</p>
                  )}
                </div>
              )}

              {selectedFlywheel.engagement && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-green-700 mb-2">Engagement</p>
                  <div className="flex gap-4 text-xs">
                    <span><Heart className="h-3 w-3 inline mr-1" />{selectedFlywheel.engagement.likes}</span>
                    <span><MessageSquare className="h-3 w-3 inline mr-1" />{selectedFlywheel.engagement.comments}</span>
                    <span><Repeat2 className="h-3 w-3 inline mr-1" />{selectedFlywheel.engagement.reposts}</span>
                  </div>
                  {selectedFlywheel.engagement.commercial_influence && (
                    <p className="text-xs text-green-600 mt-2"><Link2 className="h-3 w-3 inline mr-1" />{selectedFlywheel.engagement.commercial_influence}</p>
                  )}
                  <div className="flex gap-3 mt-1 text-[10px] text-green-600">
                    {selectedFlywheel.engagement.led_to_conversation && <span>Led to conversation</span>}
                    {selectedFlywheel.engagement.led_to_meeting && <span>Led to meeting</span>}
                    {selectedFlywheel.engagement.led_to_proposal && <span>Led to proposal</span>}
                  </div>
                </div>
              )}

              {/* Action buttons based on status */}
              <div className="flex gap-2 pt-2 border-t border-slate-100 flex-wrap">
                {selectedFlywheel.status === "draft" && !selectedFlywheel.primary_draft && (
                  <Button size="sm" onClick={() => handleGenerateDraft(selectedFlywheel.item_id)} disabled={generatingDraft} data-testid="detail-generate-btn">
                    {generatingDraft ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Brush className="h-3.5 w-3.5 mr-1" />} Generate Draft
                  </Button>
                )}
                {selectedFlywheel.status === "in_review" && (
                  <>
                    <Button size="sm" onClick={() => handleFlywheelStatus(selectedFlywheel.item_id, "ready_to_publish")} data-testid="approve-fw-btn">
                      <Check className="h-3.5 w-3.5 mr-1" /> Ready to Publish
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleFlywheelStatus(selectedFlywheel.item_id, "designer_needed")}>
                      <Brush className="h-3.5 w-3.5 mr-1" /> Needs Designer
                    </Button>
                  </>
                )}
                {selectedFlywheel.status === "designer_needed" && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => handleDesignerStatus(selectedFlywheel.item_id, "in_design")}>In Design</Button>
                    <Button size="sm" variant="outline" onClick={() => handleDesignerStatus(selectedFlywheel.item_id, "asset_ready")}>Asset Ready</Button>
                  </div>
                )}
                {selectedFlywheel.status === "ready_to_publish" && (
                  <Button size="sm" onClick={() => { handlePublish(selectedFlywheel.item_id); setShowFlywheelDetail(false); }} data-testid="publish-fw-btn">
                    <Send className="h-3.5 w-3.5 mr-1" /> Publish
                  </Button>
                )}
                {selectedFlywheel.status === "published" && (
                  <Button size="sm" variant="outline" onClick={() => {
                    setEngagementForm({ likes: 0, comments: 0, reposts: 0, linked_client_id: "", linked_opportunity_id: "", commercial_influence: "", led_to_conversation: false, led_to_meeting: false, led_to_proposal: false });
                    setShowEngagementModal(true);
                  }} data-testid="record-engagement-btn">
                    <BarChart3 className="h-3.5 w-3.5 mr-1" /> Record Engagement
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Engagement Recording Modal */}
      <Dialog open={showEngagementModal} onOpenChange={setShowEngagementModal}>
        <DialogContent className="max-w-md" data-testid="engagement-modal">
          <DialogHeader>
            <DialogTitle>Record Engagement & Commercial Influence</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Likes</label>
                <Input type="number" value={engagementForm.likes} onChange={e => setEngagementForm(p => ({ ...p, likes: parseInt(e.target.value) || 0 }))}
                  data-testid="eng-likes-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Comments</label>
                <Input type="number" value={engagementForm.comments} onChange={e => setEngagementForm(p => ({ ...p, comments: parseInt(e.target.value) || 0 }))}
                  data-testid="eng-comments-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Reposts</label>
                <Input type="number" value={engagementForm.reposts} onChange={e => setEngagementForm(p => ({ ...p, reposts: parseInt(e.target.value) || 0 }))}
                  data-testid="eng-reposts-input" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Commercial Influence</label>
              <Textarea value={engagementForm.commercial_influence} onChange={e => setEngagementForm(p => ({ ...p, commercial_influence: e.target.value }))}
                placeholder="Did this content influence any client conversation, warm reply, meeting opening, or proposal?" rows={2}
                data-testid="eng-influence-input" />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-slate-600">Downstream Impact</label>
              <div className="flex flex-col gap-1.5">
                {[
                  { key: "led_to_conversation", label: "Led to client conversation" },
                  { key: "led_to_meeting", label: "Led to meeting / call" },
                  { key: "led_to_proposal", label: "Reinforced or triggered proposal" },
                ].map(item => (
                  <label key={item.key} className="flex items-center gap-2 text-xs text-slate-600">
                    <input type="checkbox" className="rounded" checked={engagementForm[item.key]}
                      onChange={e => setEngagementForm(p => ({ ...p, [item.key]: e.target.checked }))}
                      data-testid={`eng-${item.key}`} />
                    {item.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEngagementModal(false)}>Cancel</Button>
            <Button onClick={handleRecordEngagement} data-testid="submit-engagement-btn">
              Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
